import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Menu,
  MenuItem as MuiMenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MapIcon from '@mui/icons-material/Map';
import PeopleIcon from '@mui/icons-material/People';
import LayersIcon from '@mui/icons-material/Layers';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PlaceIcon from '@mui/icons-material/Place';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import paths from '@src/paths';

import api from '@src/api/clients';
import {
  createZipSubmission,
  cancelZipSubmission,
  bulkCreateZipSubmissions,
  revertZipSubmission,
  checkZipOverlap,
  exportZipCoverageCsv,
  fetchZipSubmissions,
  fetchZipAutoReviewConfig,
  fetchLatestByTerritory,
  fetchClientPauseEvents,
  fetchTerritoryPauseHistory,
  pauseTerritory,
} from '@src/api/actions/dashboard/zipSubmissions';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import useParentCompanyAutocomplete from '@src/hooks/useParentCompanyAutocomplete';
import useLocalStorage from '@src/hooks/useLocalStorage';
import ClientZipMap from './components/ClientZipMap';
import SubmissionDiffMap from './components/SubmissionDiffMap';
import BulkZipUploadDialog from './components/BulkZipUploadDialog';

const STATUS_CONFIG = {
  pending: { color: 'warning', label: 'Pending' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
  cancelled: { color: 'default', label: 'Cancelled' },
};

// Presentation state for a *pending* submission under risk-tiered auto-review:
// instant/alert batches are queued to auto-apply (removal-bearing ones after
// an undo window — auto_applies_at), hold is the legacy staff-review path.
// The tier is stamped by a background task moments after submit, so a null
// tier falls back to the generic pending wording (returns null).
const pendingAutoState = (sub, nowMs) => {
  if (!sub || sub.status !== 'pending') return null;
  if (sub.auto_tier === 'hold') return { kind: 'hold' };
  if (sub.auto_tier === 'instant' || sub.auto_tier === 'alert') {
    const appliesMs = sub.auto_applies_at ? new Date(sub.auto_applies_at).getTime() : null;
    if (appliesMs && appliesMs > nowMs) {
      return {
        kind: 'countdown',
        minutesLeft: Math.max(1, Math.ceil((appliesMs - nowMs) / 60000)),
      };
    }
    return { kind: 'imminent' };
  }
  return null;
};

const WALKTHROUGH_STEPS = [
  {
    ref: 'addVerticalBtn',
    title: 'Add Verticals',
    description: 'If you are a new client or launching a new vertical, use this to add verticals to your account. Each vertical will appear as an option in your territories.',
  },
  {
    ref: 'newTerritoryBtn',
    title: 'Create Territories',
    description: 'Create territories to organize your ZIP coverage by region. Each territory can have multiple verticals with their own ZIPs.',
  },
  {
    ref: 'statsBar',
    title: 'Coverage at a Glance',
    description: 'See your total territories, ZIPs, active verticals, and pending submissions in one place.',
  },
  {
    ref: 'zipSearch',
    title: 'Find a ZIP',
    description: 'Quickly search to find which territory and vertical a specific ZIP code belongs to.',
  },
  {
    ref: 'territoryCard',
    title: 'Manage Your Territories',
    description: 'Expand a territory to see its verticals and ZIPs. Use "Manage ZIPs" to add or remove ZIPs, or the dropdown to add a new vertical.',
  },
  {
    ref: 'historyTab',
    title: 'Submission History',
    description: "Track all your past submissions here — see each change's status, and revert a change if you need to.",
  },
];

function WelcomeOverlay({ onStartTour, onSkip }) {
  return (
    <Dialog open fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: '#111827', color: '#fff', px: 4, pt: 4, pb: 3, textAlign: 'center' }}>
        <MapIcon sx={{ fontSize: 48, mb: 1, color: '#ff680d' }} />
        <Typography variant="h5" fontWeight={700}>
          Welcome to ZIP Coverage
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.7)' }}>
          Manage your service areas, territories, and verticals all in one place.
        </Typography>
      </Box>
      <DialogContent sx={{ px: 4, py: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Here's what you can do:
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <StepRow number={1} text="Create territories to organize your coverage by region" />
          <StepRow number={2} text="Add verticals (service types) to each territory" />
          <StepRow number={3} text="Manage ZIP codes — add, remove, or replace in bulk" />
          <StepRow number={4} text="Track your submissions and see approval status" />
          <StepRow number={5} text="View your coverage on an interactive map" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button variant="outlined" onClick={onSkip} sx={{ minWidth: 120 }}>
          Skip
        </Button>
        <Button variant="contained" onClick={onStartTour} sx={{ minWidth: 160, bgcolor: '#ff680d', '&:hover': { bgcolor: '#e55a00' } }}>
          Take a Quick Tour
        </Button>
      </DialogActions>
    </Dialog>
  );
}



function WalkthroughOverlay({ stepIndex, steps, stepRefs, onNext, onBack, onSkip }) {
  const step = steps[stepIndex];
  const ref = stepRefs[step?.ref];
  const el = ref?.current;
  const [rect, setRect] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => {
        setRect(el.getBoundingClientRect());
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [el, stepIndex]);

  if (!step || !el || !rect) return null;

  const isLast = stepIndex === steps.length - 1;
  const tooltipHeight = 180;
  const spaceBelow = window.innerHeight - rect.bottom - 16;
  const placeAbove = spaceBelow < tooltipHeight && rect.top > tooltipHeight;
  const tooltipTop = placeAbove ? rect.top - tooltipHeight - 16 : rect.bottom + 16;

  return (
    <>
      <Box
        onClick={onSkip}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.45)',
          zIndex: 1300,
          cursor: 'pointer',
        }}
      />
      <Box
        sx={{
          position: 'fixed',
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'primary.main',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
          zIndex: 1301,
          pointerEvents: 'none',
        }}
      />
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: tooltipTop,
          left: Math.max(16, Math.min(rect.left, window.innerWidth - 380)),
          width: 360,
          zIndex: 1302,
          p: 2.5,
          borderRadius: 2,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>{step.title}</Typography>
            <Typography color="text.secondary" variant="caption">
              {stepIndex + 1} / {steps.length}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">{step.description}</Typography>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button size="small" variant="text" onClick={onSkip}>Skip tour</Button>
            <Stack direction="row" spacing={1}>
              {stepIndex > 0 && (
                <Button size="small" variant="text" onClick={onBack}>Back</Button>
              )}
              <Button size="small" variant="contained" onClick={onNext}>
                {isLast ? 'Done' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

export default function ZipCoveragePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const user = state?.user;
  const memberships = user?.client_memberships || [];
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  // Detect parent-company-scoped membership
  const isParentScopeMember = !isStaff && memberships.length === 1
    && !memberships[0]?.client_id && Boolean(memberships[0]?.parent_company_id);
  const memberParentCompanyId = isParentScopeMember
    ? String(memberships[0].parent_company_id) : '';

  // Parent company filter (staff can pick any; parent-scope members are locked to theirs)
  const {
    options: parentCompanyOptions,
    findOptionByValue: findParentCompanyByValue,
  } = useParentCompanyAutocomplete({ skip: !isStaff && !isParentScopeMember });
  const [selectedParentCompanyId, setSelectedParentCompanyId] = useState(
    memberParentCompanyId || ''
  );

  // Client selection — filtered by parent company when set
  const {
    options: clientOptions,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete({
    parentCompanyId: selectedParentCompanyId || undefined,
  });

  const initialClientId = searchParams.get('client_id')
    || (memberships.length === 1 && memberships[0]?.client_id
      ? String(memberships[0].client_id)
      : '');
  const [selectedClientId, setSelectedClientId] = useState(initialClientId);

  // Parent-company "all clients" mode: a parent company is selected but no
  // specific client — show every child client's territories in one combined
  // view (read-only; pick a client to make changes).
  const isAllClientsView = Boolean(selectedParentCompanyId) && !selectedClientId;
  const hasScope = Boolean(selectedClientId) || isAllClientsView;
  const scopeQuery = selectedClientId
    ? `client_id=${selectedClientId}`
    : selectedParentCompanyId
      ? `parent_company_id=${selectedParentCompanyId}`
      : '';

  // Walkthrough
  const [walkthroughSeen, setWalkthroughSeen] = useLocalStorage('zipCoverage.walkthroughSeen', false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const walkthroughRefs = {
    addVerticalBtn: useRef(null),
    newTerritoryBtn: useRef(null),
    statsBar: useRef(null),
    zipSearch: useRef(null),
    territoryCard: useRef(null),
    historyTab: useRef(null),
  };

  // Data
  const [territories, setTerritories] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [allVerticals, setAllVerticals] = useState([]);
  const [addedVerticalIds, setAddedVerticalIds] = useState(new Set());
  const [addVerticalDialogOpen, setAddVerticalDialogOpen] = useState(false);
  const [zipsByTerritoryVertical, setZipsByTerritoryVertical] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [latestByTerritory, setLatestByTerritory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tabs & History
  const [activeTab, setActiveTab] = useState(0);
  const [historySubmissions, setHistorySubmissions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilter, setHistoryFilter] = useState('all');

  // ZIP Search
  const [zipSearchText, setZipSearchText] = useState('');

  // Territory creation
  const [newTerritoryDialogOpen, setNewTerritoryDialogOpen] = useState(false);
  const [newTerritoryName, setNewTerritoryName] = useState('');
  const [newTerritoryError, setNewTerritoryError] = useState('');
  const [newTerritorySaving, setNewTerritorySaving] = useState(false);

  // Territory rename
  const [renameTerritoryDialogOpen, setRenameTerritoryDialogOpen] = useState(false);
  const [renameTerritory, setRenameTerritory] = useState(null);
  const [renameTerritoryName, setRenameTerritoryName] = useState('');
  const [renameTerritoryError, setRenameTerritoryError] = useState('');
  const [renameTerritorySaving, setRenameTerritorySaving] = useState(false);

  // Territory delete

  // Manage ZIPs dialog
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageTerritory, setManageTerritory] = useState(null);
  // Verticals being edited. Single-element array = legacy per-vertical edit;
  // multi-element = "applies to all of these verticals" (fan-out on submit).
  const [manageVerticals, setManageVerticals] = useState([]);
  const [manageZipText, setManageZipText] = useState('');
  const [manageMode, setManageMode] = useState('add'); // 'add' | 'replace' | 'remove' | 'move'
  const [manageRemoveIds, setManageRemoveIds] = useState([]);
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const [manageError, setManageError] = useState('');
  // Set when an Add submit is blocked because ZIPs live in other territories —
  // offers a one-click "move them here instead" instead of a dead-end error.
  const [manageMoveOffer, setManageMoveOffer] = useState(null); // { addZips: [] }
  const [manageValidation, setManageValidation] = useState(null);
  const [manageValidating, setManageValidating] = useState(false);
  const [submitterNotes, setSubmitterNotes] = useState('');
  // "When should this market start?" — blank means as soon as approved.
  const [launchOn, setLaunchOn] = useState('');
  const [copyFromAnchorEl, setCopyFromAnchorEl] = useState(null);
  const [customizeAnchorEl, setCustomizeAnchorEl] = useState(null);
  const [historyMapOpenIds, setHistoryMapOpenIds] = useState(() => new Set());
  const [expandedCountyKeys, setExpandedCountyKeys] = useState(() => new Set());
  const [expandedVerticalCountiesKeys, setExpandedVerticalCountiesKeys] = useState(() => new Set());
  const [expandedTerritoryIds, setExpandedTerritoryIds] = useState(null);
  const [groupManageMenu, setGroupManageMenu] = useState(null); // { anchorEl, territory, verticals }
  const validateTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // More actions menu
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

  // Pause territory confirmation dialog
  const [pauseTogglingId, setPauseTogglingId] = useState(null); // territory id mid-toggle
  // Territory queued for the pause-confirm dialog (pause is instant + no
  // review, so we explain what it does — and doesn't do — before firing).
  const [pauseConfirmTerritory, setPauseConfirmTerritory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // territory pending deletion (staff)
  const [deleteSaving, setDeleteSaving] = useState(false);

  // Cancel submission dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  // History-tab revert: the approved submission queued for the confirm dialog.
  const [revertTarget, setRevertTarget] = useState(null);
  const [revertSubmitting, setRevertSubmitting] = useState(false);
  const [cancelSubmissionId, setCancelSubmissionId] = useState(null);
  const [cancelSaving, setCancelSaving] = useState(false);

  // Bulk upload dialog
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  // Submission confirmation dialog
  const [confirmationDialog, setConfirmationDialog] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // Auto-review config — gates every "applies automatically" string. Until it
  // loads (or when the ZIP_AUTO_REVIEW_ENABLED kill-switch is off) the page
  // keeps the legacy review-queue wording.
  const [autoReviewCfg, setAutoReviewCfg] = useState(null);
  useEffect(() => {
    fetchZipAutoReviewConfig()
      .then((res) => setAutoReviewCfg(res.data || null))
      .catch(() => setAutoReviewCfg(null));
  }, []);
  const autoReviewOn = Boolean(autoReviewCfg?.enabled);
  const undoWindowMin = autoReviewCfg?.apply_delay_minutes || 15;

  // Tick every 30s while a pending batch is queued to auto-apply so the
  // "Applying in ~N min" labels count down; once a deadline passes, refresh
  // so the applied change moves out of pending and into coverage/history.
  const [nowTs, setNowTs] = useState(() => Date.now());

  // ─── Data Loading ──────────────────────────────────────────────────

  const loadTerritories = useCallback(async () => {
    if (!scopeQuery) { setTerritories([]); return; }
    try {
      const res = await api.request({
        url: `dashboard/client-territories/?${scopeQuery}`,
        method: 'GET',
      });
      const rows = (res.data || []).slice().sort((a, b) =>
        (a.client_name || '').localeCompare(b.client_name || '', undefined, { sensitivity: 'base' })
        || (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true })
      );
      setTerritories(rows);
    } catch { setTerritories([]); }
  }, [scopeQuery]);

  const loadVerticals = useCallback(async () => {
    if (!scopeQuery) { setVerticals([]); return; }
    try {
      const res = await api.request({
        url: `dashboard/client-zips/vertical_options/?${scopeQuery}`,
        method: 'GET',
      });
      setVerticals(res.data || []);
    } catch { setVerticals([]); }
  }, [scopeQuery]);

  const loadAllVerticals = useCallback(async () => {
    try {
      const res = await api.request({
        url: 'dashboard/verticals/all-options/',
        method: 'GET',
      });
      setAllVerticals(res.data || []);
    } catch { setAllVerticals([]); }
  }, []);

  const loadZips = useCallback(async () => {
    if (!scopeQuery) { setZipsByTerritoryVertical({}); return; }
    setLoading(true);
    try {
      const res = await api.request({
        url: `dashboard/client-zips/coverage_summary/?${scopeQuery}`,
        method: 'GET',
      });
      const grouped = {};
      (res.data || []).forEach((czv) => {
        const key = `${czv.client_territory_id || 'none'}__${czv.vertical_id}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(czv);
      });
      setZipsByTerritoryVertical(grouped);
    } catch { setZipsByTerritoryVertical({}); }
    finally { setLoading(false); }
  }, [scopeQuery]);

  const scopeParams = useMemo(() => {
    if (selectedClientId) return { client_id: selectedClientId };
    if (selectedParentCompanyId) return { parent_company_id: selectedParentCompanyId };
    return null;
  }, [selectedClientId, selectedParentCompanyId]);

  const loadSubmissions = useCallback(async () => {
    if (!scopeParams) { setSubmissions([]); return; }
    try {
      const res = await fetchZipSubmissions({ ...scopeParams, status: 'pending' });
      setSubmissions(res.data?.results || []);
    } catch { setSubmissions([]); }
  }, [scopeParams]);

  const loadLatestByTerritory = useCallback(async () => {
    if (!scopeParams) { setLatestByTerritory([]); return; }
    try {
      const res = await fetchLatestByTerritory(scopeParams);
      setLatestByTerritory(res.data || []);
    } catch { setLatestByTerritory([]); }
  }, [scopeParams]);

  const loadHistory = useCallback(async (page = 1, statusFilter = 'all', append = false) => {
    if (!scopeParams) { setHistorySubmissions([]); return; }
    setHistoryLoading(true);
    try {
      const params = { ...scopeParams, page, page_size: 25 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await fetchZipSubmissions(params);
      const results = res.data?.results || [];
      setHistorySubmissions((prev) => append ? [...prev, ...results] : results);
      setHistoryHasMore(!!res.data?.next);
      setHistoryPage(page);
    } catch { if (!append) setHistorySubmissions([]); }
    finally { setHistoryLoading(false); }
  }, [scopeParams]);

  useEffect(() => { loadAllVerticals(); }, [loadAllVerticals]);

  useEffect(() => {
    setAddedVerticalIds(new Set());
    setActiveTab(0);
    setZipSearchText('');
    setHistoryFilter('all');
    loadTerritories();
    loadVerticals();
    loadZips();
    loadSubmissions();
    loadLatestByTerritory();
    setExpandedTerritoryIds(null);
  }, [selectedClientId, selectedParentCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown re-render for auto-applying batches (see nowTs above). A plain
  // interval is enough — the chips only show whole minutes.
  useEffect(() => {
    if (!submissions.some((s) => s.auto_applies_at)) return undefined;
    const id = setInterval(() => {
      setNowTs(Date.now());
      const overdue = submissions.some(
        (s) => s.auto_applies_at && new Date(s.auto_applies_at).getTime() <= Date.now(),
      );
      if (overdue) {
        loadSubmissions();
        loadZips();
      }
    }, 30000);
    return () => clearInterval(id);
  }, [submissions, loadSubmissions, loadZips]);

  // Pause/resume activity shown alongside submission history (client scope only).
  const [pauseEvents, setPauseEvents] = useState([]);

  useEffect(() => {
    if (activeTab === 2 && hasScope) {
      setHistoryPage(1);
      loadHistory(1, historyFilter);
      if (selectedClientId) {
        fetchClientPauseEvents(Number(selectedClientId))
          .then((res) => setPauseEvents(res?.data?.events || []))
          .catch(() => setPauseEvents([]));
      } else {
        setPauseEvents([]);
      }
    }
  }, [activeTab, selectedClientId, selectedParentCompanyId, historyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Walkthrough ─────────────────────────────────────────────────

  useEffect(() => {
    if (!walkthroughSeen && !loading && selectedClientId && !walkthroughActive && !welcomeOpen) {
      const timer = setTimeout(() => {
        setWelcomeOpen(true);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [walkthroughSeen, loading, selectedClientId, walkthroughActive, welcomeOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWelcomeStartTour = useCallback(() => {
    setWelcomeOpen(false);
    const firstValid = WALKTHROUGH_STEPS.findIndex(
      (s) => walkthroughRefs[s.ref]?.current
    );
    if (firstValid >= 0) {
      setWalkthroughStep(firstValid);
      setWalkthroughActive(true);
    } else {
      setWalkthroughSeen(true);
    }
  }, [setWalkthroughSeen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWelcomeSkip = useCallback(() => {
    setWelcomeOpen(false);
    setWalkthroughSeen(true);
  }, [setWalkthroughSeen]);

  const endWalkthrough = useCallback(() => {
    setWalkthroughActive(false);
    setWalkthroughStep(0);
    setWalkthroughSeen(true);
  }, [setWalkthroughSeen]);

  const nextWalkthroughStep = useCallback(() => {
    let next = walkthroughStep + 1;
    // Skip steps whose ref element doesn't exist
    while (next < WALKTHROUGH_STEPS.length) {
      const ref = walkthroughRefs[WALKTHROUGH_STEPS[next].ref];
      if (ref?.current) break;
      next += 1;
    }
    if (next >= WALKTHROUGH_STEPS.length) {
      endWalkthrough();
    } else {
      setWalkthroughStep(next);
    }
  }, [endWalkthrough, walkthroughStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevWalkthroughStep = useCallback(() => {
    let prev = walkthroughStep - 1;
    while (prev >= 0) {
      const ref = walkthroughRefs[WALKTHROUGH_STEPS[prev].ref];
      if (ref?.current) break;
      prev -= 1;
    }
    setWalkthroughStep(Math.max(0, prev));
  }, [walkthroughStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Derived Data ──────────────────────────────────────────────────

  const availableVerticals = useMemo(() => {
    const clientIds = new Set(verticals.map((v) => v.id));
    const extra = allVerticals.filter(
      (v) => addedVerticalIds.has(v.id) && !clientIds.has(v.id)
    );
    return [...verticals, ...extra];
  }, [verticals, allVerticals, addedVerticalIds]);

  const verticalById = useMemo(() => {
    const map = {};
    allVerticals.forEach((v) => { map[v.id] = v; });
    availableVerticals.forEach((v) => { map[v.id] = v; });
    return map;
  }, [allVerticals, availableVerticals]);

  const territoryVerticals = useMemo(() => {
    const map = {};
    territories.forEach((t) => { map[t.id] = new Set(); });
    Object.keys(zipsByTerritoryVertical).forEach((key) => {
      const [tId, vId] = key.split('__');
      if (map[tId]) map[tId].add(Number(vId));
    });
    return map;
  }, [territories, zipsByTerritoryVertical]);

  const pendingByTerritoryVertical = useMemo(() => {
    const map = {};
    submissions.forEach((sub) => {
      const key = `${sub.client_territory || 'none'}__${sub.vertical}`;
      if (!map[key]) map[key] = [];
      map[key].push(sub);
    });
    return map;
  }, [submissions]);

  const territoriesWithPending = useMemo(() => {
    const set = new Set();
    submissions.forEach((sub) => {
      if (sub.client_territory) set.add(sub.client_territory);
    });
    return set;
  }, [submissions]);

  const latestByTerritoryMap = useMemo(() => {
    const map = {};
    (latestByTerritory || []).forEach((row) => {
      const tId = row.territory_id;
      if (!tId) return;
      const existing = map[tId];
      const when = row.reviewed_at ? new Date(row.reviewed_at) : null;
      if (!when) return;
      if (!existing || when > existing.when) {
        map[tId] = {
          when,
          reviewedBy: row.reviewed_by_email,
          submittedBy: row.submitted_by_email,
        };
      }
    });
    return map;
  }, [latestByTerritory]);

  const stats = useMemo(() => {
    const allZips = Object.values(zipsByTerritoryVertical);
    const totalZips = allZips.reduce((sum, arr) => sum + arr.length, 0);
    const standardZips = allZips.reduce(
      (sum, arr) => sum + arr.filter((z) => (z.zip_type || '').toUpperCase() === 'STANDARD').length,
      0,
    );
    const totalPopulation = allZips
      .reduce((sum, arr) => sum + arr.reduce((s, z) => s + (z.population || 0), 0), 0);
    const standardPopulation = allZips.reduce(
      (sum, arr) => sum + arr.reduce(
        (s, z) => s + ((z.zip_type || '').toUpperCase() === 'STANDARD' ? (z.population || 0) : 0),
        0,
      ),
      0,
    );
    const activeVerticalIds = new Set();
    Object.keys(zipsByTerritoryVertical).forEach((key) => {
      activeVerticalIds.add(key.split('__')[1]);
    });
    return {
      territories: territories.length,
      totalZips,
      standardZips,
      totalPopulation,
      standardPopulation,
      verticals: activeVerticalIds.size,
      pending: submissions.length,
    };
  }, [territories, zipsByTerritoryVertical, submissions]);

  const zipSearchResults = useMemo(() => {
    const q = zipSearchText.trim();
    if (!q || q.length < 2) return null;
    const results = [];
    for (const [key, zips] of Object.entries(zipsByTerritoryVertical)) {
      const [tId, vId] = key.split('__');
      const matching = zips.filter((czv) => czv.zip.includes(q));
      if (matching.length > 0) {
        const territory = territories.find((t) => String(t.id) === tId);
        const vertical = verticalById[Number(vId)];
        results.push({
          client: territory?.client_name || matching[0]?.client_name || '',
          territory: territory?.name || 'Unknown',
          vertical: vertical?.vertical || 'Unknown',
          zips: matching.map((czv) => czv.zip),
        });
      }
    }
    return results;
  }, [zipSearchText, zipsByTerritoryVertical, territories, verticalById]);

  // Parent-company all-clients view: territories grouped per client.
  const clientGroups = useMemo(() => {
    if (!isAllClientsView) return [];
    const byClient = new Map();
    territories.forEach((t) => {
      const clientId = t.client;
      if (!byClient.has(clientId)) {
        byClient.set(clientId, {
          clientId,
          clientName: t.client_name || `Client ${clientId}`,
          territories: [],
        });
      }
      byClient.get(clientId).territories.push(t);
    });
    return [...byClient.values()].sort((a, b) =>
      a.clientName.localeCompare(b.clientName, undefined, { sensitivity: 'base' })
    );
  }, [isAllClientsView, territories]);

  // ─── Territory Creation ────────────────────────────────────────────

  const handleCreateTerritory = async () => {
    const name = newTerritoryName.trim();
    if (!name) {
      setNewTerritoryError('Territory name is required.');
      return;
    }
    const existing = territories.find(
      (t) => t.name.toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      setNewTerritoryError(`Territory "${existing.name}" already exists.`);
      return;
    }
    setNewTerritorySaving(true);
    setNewTerritoryError('');
    try {
      await api.request({
        url: 'dashboard/client-territories/',
        method: 'POST',
        payload: { client: selectedClientId, name },
      });
      setNewTerritoryDialogOpen(false);
      setNewTerritoryName('');
      loadTerritories();
      showSnack(`Territory "${name}" created.`);
    } catch (err) {
      const detail = err?.response?.data?.name?.[0]
        || err?.response?.data?.detail
        || 'Failed to create territory.';
      setNewTerritoryError(String(detail));
    } finally {
      setNewTerritorySaving(false);
    }
  };

  // ─── Territory Rename ──────────────────────────────────────────────

  const handleOpenRename = (territory) => {
    setRenameTerritory(territory);
    setRenameTerritoryName(territory.name);
    setRenameTerritoryError('');
    setRenameTerritoryDialogOpen(true);
  };

  const handleRenameTerritory = async () => {
    const name = renameTerritoryName.trim();
    if (!name) {
      setRenameTerritoryError('Territory name is required.');
      return;
    }
    if (name === renameTerritory.name) {
      setRenameTerritoryDialogOpen(false);
      return;
    }
    setRenameTerritorySaving(true);
    setRenameTerritoryError('');
    try {
      await api.request({
        url: `dashboard/client-territories/${renameTerritory.id}/`,
        method: 'PATCH',
        payload: { name },
      });
      setRenameTerritoryDialogOpen(false);
      loadTerritories();
      showSnack(`Territory renamed to "${name}".`);
    } catch (err) {
      const detail = err?.response?.data?.name?.[0]
        || err?.response?.data?.detail
        || 'Failed to rename territory.';
      setRenameTerritoryError(String(detail));
    } finally {
      setRenameTerritorySaving(false);
    }
  };


  // ─── Manage ZIPs ──────────────────────────────────────────────────

  // Pass a single vertical OR an array. Multi-vertical = fan-out on submit.
  const handleOpenManageDialog = (territory, verticalOrList) => {
    const list = Array.isArray(verticalOrList) ? verticalOrList : [verticalOrList];
    setManageTerritory(territory);
    setManageVerticals(list.filter(Boolean));
    setManageZipText('');
    setManageMode('add');
    setManageRemoveIds([]);
    setManageError('');
    setManageMoveOffer(null);
    setManageValidation(null);
    setManageValidating(false);
    setSubmitterNotes('');
    setCopyFromAnchorEl(null);
    setCustomizeAnchorEl(null);
    setManageDialogOpen(true);
  };

  // ── Dashboard quick-action deep link (?action=add|remove|move|replace) ──
  // With exactly one territory we open Manage ZIPs on that tab directly;
  // otherwise we point the user at the territory list. Handled once per page
  // load — the param is stripped so a refresh doesn't re-trigger it.
  const quickActionRef = useRef(searchParams.get('action') || '');
  useEffect(() => {
    const quickAction = quickActionRef.current;
    if (!quickAction) return;
    const stripParam = () => setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('action');
      return next;
    }, { replace: true });
    if (!['add', 'remove', 'move', 'replace'].includes(quickAction)) {
      quickActionRef.current = '';
      stripParam();
      return;
    }
    // Wait for a client scope and for coverage data (territoryVerticals
    // derives from the coverage summary, which `loading` tracks).
    if (!selectedClientId || loading || territories.length === 0) return;
    quickActionRef.current = '';
    stripParam();
    if (territories.length === 1) {
      const territory = territories[0];
      const tVerts = [...(territoryVerticals[territory.id] || [])]
        .map((id) => verticalById[id])
        .filter(Boolean);
      // Mirror the territory-level Manage ZIPs button: single vertical, or
      // multi-vertical when every vertical shares the same ZIP set.
      const sigs = new Set(
        tVerts.map((v) => (zipsByTerritoryVertical[`${territory.id}__${v.id}`] || [])
          .map((z) => z.zip).sort().join(',')),
      );
      const target = tVerts.length === 1
        ? tVerts[0]
        : (tVerts.length > 1 && sigs.size === 1 ? tVerts : null);
      if (target) {
        handleOpenManageDialog(territory, target);
        setManageMode(quickAction);
        return;
      }
    }
    walkthroughRefs.territoryCard.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const verb = {
      add: 'add coverage',
      remove: 'remove ZIPs',
      move: 'move ZIPs',
      replace: 'replace the ZIP list',
    }[quickAction];
    showSnack(`Choose a territory, then Manage ZIPs to ${verb}.`, 'info');
  }, [selectedClientId, loading, territories, territoryVerticals, verticalById, zipsByTerritoryVertical]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMultiVerticalManage = manageVerticals.length > 1;
  // Anchor vertical drives "current ZIPs" display + overlap checks. For multi,
  // we use the first vertical (consolidated groups already share ZIP sets).
  const manageAnchorVertical = manageVerticals[0] || null;

  const currentManagedZips = useMemo(() => {
    if (!manageTerritory || !manageAnchorVertical) return [];
    const key = `${manageTerritory.id}__${manageAnchorVertical.id}`;
    return zipsByTerritoryVertical[key] || [];
  }, [manageTerritory, manageAnchorVertical, zipsByTerritoryVertical]);

  // Pre-submit change preview for the diff map: adds (green) come from the
  // overlap check's zip_details coordinates, removes (red) from the already
  // loaded coverage rows. Coordinates are progressive enhancement — ZIPs
  // without them simply don't render a marker.
  const manageDiffMapItems = useMemo(() => {
    if (!manageValidation) return [];
    const items = [];
    const details = manageValidation.zipDetails || {};
    const currentByZip = {};
    currentManagedZips.forEach((czv) => { currentByZip[czv.zip] = czv; });
    const pushAdd = (z) => {
      const d = details[z];
      if (d?.latitude != null && d?.longitude != null) {
        items.push({
          id: `add-${z}`,
          zip: z,
          action: 'add',
          latitude: Number(d.latitude),
          longitude: Number(d.longitude),
          city: d.city || '',
        });
      }
    };
    const pushRemove = (czv) => {
      if (czv && czv.latitude != null && czv.longitude != null) {
        items.push({
          id: `rm-${czv.zip}`,
          zip: czv.zip,
          action: 'remove',
          latitude: Number(czv.latitude),
          longitude: Number(czv.longitude),
          city: czv.city || '',
        });
      }
    };
    if (manageMode === 'remove') {
      (manageValidation.matched || []).forEach((z) => pushRemove(currentByZip[z]));
    } else if (manageMode === 'replace') {
      (manageValidation.newZips || []).forEach(pushAdd);
      (manageValidation.toRemove || []).forEach(pushRemove);
    } else {
      (manageValidation.newZips || []).forEach(pushAdd);
    }
    return items;
  }, [manageValidation, manageMode, currentManagedZips]);

  // Options for "Copy from another vertical" — any (territory, vertical) bucket
  // that isn't currently being edited and has at least one standard ZIP.
  const copySourceOptions = useMemo(() => {
    if (!manageTerritory || manageVerticals.length === 0) return [];
    const excludedKeys = new Set(
      manageVerticals.map((v) => `${manageTerritory.id}__${v.id}`),
    );
    const options = [];
    Object.entries(zipsByTerritoryVertical).forEach(([key, zips]) => {
      if (excludedKeys.has(key)) return;
      const [tId, vId] = key.split('__');
      const standardZips = zips.filter(
        (z) => (z.zip_type || '').toUpperCase() === 'STANDARD',
      );
      if (standardZips.length === 0) return;
      const territory = territories.find((t) => String(t.id) === tId);
      const vertical = verticalById[Number(vId)];
      if (!territory || !vertical) return;
      options.push({
        key,
        territoryName: territory.name,
        verticalName: vertical.vertical,
        zips: standardZips,
      });
    });
    options.sort(
      (a, b) => a.territoryName.localeCompare(b.territoryName)
        || a.verticalName.localeCompare(b.verticalName),
    );
    return options;
  }, [manageTerritory, manageVerticals, zipsByTerritoryVertical, territories, verticalById]);

  // Live validation for the manage dialog textarea
  useEffect(() => {
    if (!manageDialogOpen) {
      setManageValidation(null);
      setManageValidating(false);
      return;
    }

    const parsed = manageZipText
      .split(/[\n,\t]+/)
      .map((z) => {
        const t = z.trim();
        return /^\d{1,4}$/.test(t) ? t.padStart(5, '0') : t;
      })
      .filter(Boolean);

    if (parsed.length === 0) {
      setManageValidation(null);
      return;
    }

    const unique = [...new Set(parsed)];
    const activeSet = new Set(currentManagedZips.map((czv) => czv.zip));

    if (manageMode === 'remove') {
      // Remove mode: pasted ZIPs that match current become removes
      const matched = unique.filter((z) => activeSet.has(z));
      const notFound = unique.filter((z) => !activeSet.has(z));
      setManageValidation({
        total: unique.length,
        inputDupes: parsed.length - unique.length,
        matched,
        notFound,
        newZips: [],
        dupes: [],
        unknown: [],
        conflicts: [],
        apiChecked: true,
      });
      setManageValidating(false);
      return;
    }

    if (manageMode === 'replace') {
      // Replace mode: new ZIPs = adds, current ZIPs not in paste = removes
      const newZips = unique.filter((z) => !activeSet.has(z));
      const kept = unique.filter((z) => activeSet.has(z));
      const toRemove = currentManagedZips.filter((czv) => !unique.includes(czv.zip));
      setManageValidation({
        total: unique.length,
        inputDupes: parsed.length - unique.length,
        dupes: kept,
        newZips,
        toRemove,
        unknown: [],
        conflicts: [],
        apiChecked: false,
      });

      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
      if (newZips.length > 0 && manageTerritory && manageAnchorVertical) {
        setManageValidating(true);
        validateTimerRef.current = setTimeout(async () => {
          try {
            const res = await checkZipOverlap({
              client_id: Number(selectedClientId),
              vertical_id: manageAnchorVertical.id,
              client_territory_id: manageTerritory.id,
              zip_codes: newZips,
            });
            setManageValidation((prev) => prev ? {
              ...prev,
              unknown: res.data?.unknown_zips || [],
              conflicts: res.data?.conflicts || [],
              zipDetails: res.data?.zip_details || {},
              apiChecked: true,
            } : prev);
          } catch {
            // silent
          } finally {
            setManageValidating(false);
          }
        }, 800);
      } else {
        setManageValidation((prev) => prev ? { ...prev, apiChecked: true } : prev);
        setManageValidating(false);
      }
      return;
    }

    // Add mode (default)
    const dupes = unique.filter((z) => activeSet.has(z));
    const newZips = unique.filter((z) => !activeSet.has(z));

    setManageValidation({
      total: unique.length,
      inputDupes: parsed.length - unique.length,
      dupes,
      newZips,
      unknown: [],
      conflicts: [],
      apiChecked: false,
    });

    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);

    if (newZips.length > 0 && manageTerritory && manageAnchorVertical) {
      setManageValidating(true);
      validateTimerRef.current = setTimeout(async () => {
        try {
          const res = await checkZipOverlap({
            client_id: Number(selectedClientId),
            vertical_id: manageAnchorVertical.id,
            client_territory_id: manageTerritory.id,
            zip_codes: newZips,
          });
          setManageValidation((prev) => prev ? {
            ...prev,
            unknown: res.data?.unknown_zips || [],
            conflicts: res.data?.conflicts || [],
            zipDetails: res.data?.zip_details || {},
            apiChecked: true,
          } : prev);
        } catch {
          // API check failed silently — submit will catch it
        } finally {
          setManageValidating(false);
        }
      }, 800);
    } else {
      setManageValidating(false);
    }

    return () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    };
  }, [manageZipText, manageMode, manageDialogOpen, currentManagedZips, selectedClientId, manageTerritory, manageAnchorVertical]);

  // CSV file upload handler
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result || '';
      // Parse CSV: extract anything that looks like a ZIP (digits, allow leading zeros)
      const zips = text
        .split(/[\n\r,\t;|]+/)
        .map((s) => s.replace(/["']/g, '').trim())
        .filter((s) => /^\d{3,10}$/.test(s));
      if (zips.length === 0) {
        setManageError('No valid ZIP codes found in the file.');
        return;
      }
      setManageZipText((prev) => {
        const existing = prev.trim();
        return existing ? `${existing}\n${zips.join('\n')}` : zips.join('\n');
      });
      showSnack(`${zips.length} ZIP codes loaded from file.`);
    };
    reader.readAsText(file);
    // Reset so same file can be re-uploaded
    e.target.value = '';
  };

  const handleToggleRemove = (zipId) => {
    setManageRemoveIds((prev) =>
      prev.includes(zipId) ? prev.filter((id) => id !== zipId) : [...prev, zipId]
    );
  };

  const handleRemoveInvalidZips = () => {
    const badSet = new Set(manageValidation?.unknown || []);
    if (!badSet.size) return;
    const cleaned = manageZipText
      .split(/[\n,\t]+/)
      .map((z) => z.trim())
      .filter((z) => z && !badSet.has(z))
      .join('\n');
    setManageZipText(cleaned);
  };

  // Submit ZIPs as a MOVE into this territory: any ZIP currently assigned to a
  // different territory gets a paired remove-from-source + add-here in one
  // batch (bulk_create), so nothing is ever "in two places" and the client
  // never has to empty the old territory first. ZIPs not assigned anywhere
  // submit as plain adds. check_overlap caps conflicts at 50 per vertical;
  // any conflicted ZIP past that cap still moves correctly on approval (adds
  // re-point the assignment), it just won't show an explicit remove line.
  const submitAsMove = async (addZips) => {
    if (!addZips.length || !manageTerritory) return;
    setManageSubmitting(true);
    setManageError('');
    try {
      const checks = await Promise.all(
        manageVerticals.map((v) =>
          checkZipOverlap({
            client_id: Number(selectedClientId),
            vertical_id: v.id,
            client_territory_id: manageTerritory.id,
            zip_codes: addZips,
          }).then((res) => ({ vertical: v, data: res.data })),
        ),
      );
      const allUnknown = [...new Set(checks.flatMap((c) => c.data?.unknown_zips || []))];
      if (allUnknown.length) {
        setManageError(`Unknown ZIP codes: ${allUnknown.slice(0, 10).join(', ')}`);
        return;
      }
      const items = [];
      let moveCount = 0;
      checks.forEach(({ vertical, data }) => {
        const sourceByZip = {};
        (data?.conflicts || []).forEach((c) => {
          if (c.current_territory && c.current_territory !== 'None') {
            sourceByZip[c.zip] = c.current_territory;
          }
        });
        addZips.forEach((z) => {
          const source = sourceByZip[z];
          if (source) {
            moveCount += 1;
            items.push({
              territory_name: source,
              vertical_name: vertical.vertical,
              zip_raw: z,
              action: 'remove',
            });
          }
          items.push({
            territory_name: manageTerritory.name,
            vertical_name: vertical.vertical,
            zip_raw: z,
            action: 'add',
          });
        });
      });
      await bulkCreateZipSubmissions({
        client_id: Number(selectedClientId),
        submitter_notes: submitterNotes.trim(),
        start_on: launchOn || undefined,
        items,
      });
      setManageDialogOpen(false);
      setManageMoveOffer(null);
      const verticalLabel = manageVerticals.length > 1
        ? `${manageVerticals.length} verticals`
        : manageVerticals[0]?.vertical || '';
      setConfirmationDialog({
        territory: manageTerritory.name,
        vertical: verticalLabel,
        addCount: addZips.length,
        removeCount: moveCount,
        submissionCount: manageVerticals.length > 1 ? manageVerticals.length : undefined,
        // Moves net out to zero removals → always instant tier; the undo
        // window only applies when the batch carries removes (bulk-create
        // returns a bare list, so this is derived rather than server-sent).
        autoReview: autoReviewCfg?.enabled
          ? {
            enabled: true,
            tier: 'instant',
            delayed: moveCount > 0,
            apply_delay_minutes: undoWindowMin,
          }
          : null,
      });
      loadSubmissions();
      // Same post-commit tier-stamp lag as handleSubmitZipChanges.
      window.setTimeout(loadSubmissions, 7000);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to submit the move.';
      setManageError(String(detail));
    } finally {
      setManageSubmitting(false);
    }
  };

  const handleSubmitZipChanges = async () => {
    const parsed = manageZipText
      .split(/[\n,\t]+/)
      .map((z) => {
        const t = z.trim();
        return /^\d{1,4}$/.test(t) ? t.padStart(5, '0') : t;
      })
      .filter(Boolean);
    const unique = [...new Set(parsed)];
    const activeSet = new Set(currentManagedZips.map((czv) => czv.zip));

    let addZips = [];
    let removeZips = [];

    if (manageMode === 'remove') {
      // Remove mode: pasted ZIPs that exist in current set become removes
      removeZips = unique.filter((z) => activeSet.has(z));
      if (!removeZips.length) {
        setManageError('None of the pasted ZIPs are currently active.');
        return;
      }
    } else if (manageMode === 'replace') {
      // Replace mode: pasted = desired final set
      addZips = unique.filter((z) => !activeSet.has(z));
      removeZips = currentManagedZips
        .filter((czv) => !unique.includes(czv.zip))
        .map((czv) => czv.zip);
      if (!addZips.length && !removeZips.length) {
        setManageError('The pasted ZIPs match the current set — nothing to change.');
        return;
      }
    } else if (manageMode === 'move') {
      // Move mode: bring the pasted ZIPs into this territory from wherever
      // they currently live (paired remove+add per ZIP, one batch).
      addZips = unique.filter((z) => !activeSet.has(z));
      if (!addZips.length) {
        setManageError('All pasted ZIPs are already in this territory — nothing to move.');
        return;
      }
      await submitAsMove(addZips);
      return;
    } else {
      // Add mode: only new ZIPs (skip already active), chip-clicked = removes
      addZips = unique.filter((z) => !activeSet.has(z));
      removeZips = currentManagedZips
        .filter((czv) => manageRemoveIds.includes(czv.id))
        .map((czv) => czv.zip);
      if (!addZips.length && !removeZips.length) {
        setManageError('Add ZIPs or select ZIPs to remove.');
        return;
      }
    }

    if (addZips.length) {
      // For multi-vertical, run overlap checks per vertical so we surface
      // conflicts before round-tripping the create call.
      try {
        const checks = await Promise.all(
          manageVerticals.map((v) =>
            checkZipOverlap({
              client_id: Number(selectedClientId),
              vertical_id: v.id,
              client_territory_id: manageTerritory.id,
              zip_codes: addZips,
            }).then((res) => ({ vertical: v, data: res.data })),
          ),
        );
        const allUnknown = checks.flatMap((c) => c.data?.unknown_zips || []);
        if (allUnknown.length) {
          const uniq = [...new Set(allUnknown)];
          setManageError(`Unknown ZIP codes: ${uniq.slice(0, 10).join(', ')}`);
          return;
        }
        const blocked = checks.filter((c) => c.data?.has_conflicts);
        if (blocked.length) {
          const summary = blocked
            .slice(0, 3)
            .map((c) => {
              const detail = (c.data.conflicts || [])
                .slice(0, 3)
                .map((cf) => `${cf.zip} (in ${cf.current_territory})`)
                .join(', ');
              return manageVerticals.length > 1
                ? `${c.vertical.vertical}: ${detail}`
                : detail;
            })
            .join(' · ');
          setManageError(
            `Some of these ZIPs are already assigned to another territory: ${summary}. `
            + 'A ZIP can only belong to one territory at a time.',
          );
          // Offer the fix instead of dead-ending: move them here in one step.
          if (manageMode === 'add') {
            setManageMoveOffer({ addZips });
          }
          return;
        }
        setManageMoveOffer(null);
      } catch {
        setManageError('Failed to check for overlaps.');
        return;
      }
    }

    const items = [
      ...addZips.map((z) => ({ zip_raw: z, action: 'add' })),
      ...removeZips.map((z) => ({ zip_raw: z, action: 'remove' })),
    ];

    setManageSubmitting(true);
    setManageError('');
    try {
      const verticalIds = manageVerticals.map((v) => v.id);
      const payload = {
        client_id: Number(selectedClientId),
        client_territory_id: manageTerritory.id,
        submitter_notes: submitterNotes.trim(),
        start_on: launchOn || undefined,
        items,
      };
      if (verticalIds.length > 1) {
        payload.vertical_ids = verticalIds;
      } else {
        payload.vertical_id = verticalIds[0];
      }
      const res = await createZipSubmission(payload);
      setManageDialogOpen(false);
      const verticalLabel = manageVerticals.length > 1
        ? `${manageVerticals.length} verticals`
        : manageVerticals[0]?.vertical || '';
      setConfirmationDialog({
        territory: manageTerritory.name,
        vertical: verticalLabel,
        addCount: addZips.length,
        removeCount: removeZips.length,
        submissionCount: manageVerticals.length > 1 ? manageVerticals.length : undefined,
        autoReview: res?.data?.auto_review || null,
      });
      loadSubmissions();
      // The authoritative tier lands via a post-commit task a few seconds
      // after create; one delayed refetch surfaces the countdown chip
      // without waiting for a manual reload.
      window.setTimeout(loadSubmissions, 7000);
    } catch (err) {
      const data = err?.response?.data || {};
      let detail = data.detail
        || (data.conflicting_zips?.length
          ? `Pending conflicts: ${data.conflicting_zips.slice(0, 10).join(', ')}`
          : null)
        || (data.invalid_zips?.length
          ? `Unknown ZIP codes: ${data.invalid_zips.slice(0, 20).join(', ')}`
          : null)
        || 'Failed to submit changes.';
      if (data.verticals_blocked?.length) {
        const summary = data.verticals_blocked
          .slice(0, 3)
          .map((vb) => `${vb.vertical_name}: ${vb.detail || 'conflict'}`)
          .join(' · ');
        detail = `${detail} — ${summary}`;
      }
      setManageError(String(detail));
    } finally {
      setManageSubmitting(false);
    }
  };

  // ─── Bulk Upload ─────────────────────────────────────────────────
  // Server-driven flow (BulkZipUploadDialog): the backend guesses columns,
  // fuzzy-matches names, and previews groups; works in single-client AND
  // parent-company (all clients, requires a Client column) scope.

  const handleOpenBulkUpload = () => setBulkUploadOpen(true);

  // ─── Revert (History tab) ──────────────────────────────────────────
  const handleConfirmRevert = async () => {
    if (!revertTarget) return;
    setRevertSubmitting(true);
    try {
      await revertZipSubmission(revertTarget.id);
      setRevertTarget(null);
      showSnack('Revert submitted — it will apply and update your campaigns automatically.');
      loadHistory(1, historyFilter);
      loadSubmissions();
      loadZips();
    } catch (err) {
      showSnack(
        err?.response?.data?.detail || 'Failed to revert this change.',
        'error',
      );
    } finally {
      setRevertSubmitting(false);
    }
  };

  const handleBulkSubmitted = (result) => {
    setConfirmationDialog({
      territory: isAllClientsView
        ? `${result.client_count} client${result.client_count > 1 ? 's' : ''}`
        : `${result.submission_count} group${result.submission_count > 1 ? 's' : ''}`,
      vertical: 'Bulk Upload',
      addCount: result.total_adds,
      removeCount: result.total_removes,
      submissionCount: result.submission_count,
      autoReview: result.auto_review || null,
    });
    loadSubmissions();
    loadTerritories();
  };

  // ─── Pause / Resume Territory (instant toggle) ─────────────────────
  //
  // Pause pulls the territory's ZIPs out of Facebook targeting (the market goes
  // dark) but keeps them counting for spend attribution, so historical spend
  // stays labeled under this territory instead of falling into "Unknown".
  // Resume puts them back. To truly remove a market, staff use Delete.

  const handleTogglePause = async (territory, nextPaused, vertical = null) => {
    setPauseTogglingId(territory.id);
    try {
      const payload = {
        client_id: Number(selectedClientId),
        client_territory_id: territory.id,
        paused: nextPaused,
      };
      if (vertical) payload.vertical_id = vertical.id;
      await pauseTerritory(payload);
      const label = vertical ? `${territory.name} / ${vertical.vertical}` : territory.name;
      showSnack(
        nextPaused
          ? `${label} paused — Meta targeting updates in ~2 min (spend stays attributed).`
          : `${label} resumed — returning to Meta targeting in ~2 min.`,
      );
      await loadTerritories();
    } catch (err) {
      const detail = err?.response?.data?.detail
        || `Failed to ${nextPaused ? 'pause' : 'resume'} territory.`;
      showSnack(detail, 'error');
    } finally {
      setPauseTogglingId(null);
    }
  };

  // ─── Pause history dialog ──────────────────────────────────────────

  const [pauseHistoryTerritory, setPauseHistoryTerritory] = useState(null);
  const [pauseHistoryEvents, setPauseHistoryEvents] = useState(null); // null = loading

  const handleOpenPauseHistory = async (territory) => {
    setPauseHistoryTerritory(territory);
    setPauseHistoryEvents(null);
    try {
      const res = await fetchTerritoryPauseHistory(territory.id, Number(selectedClientId));
      setPauseHistoryEvents(res?.data?.events || []);
    } catch (err) {
      setPauseHistoryEvents([]);
      showSnack('Failed to load pause history.', 'error');
    }
  };

  // ─── Delete Market (staff only) ────────────────────────────────────

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    try {
      await api.request({
        url: `dashboard/client-territories/${deleteTarget.id}/delete-market/`,
        method: 'POST',
        payload: { client_id: Number(selectedClientId) },
      });
      showSnack(`${deleteTarget.name} deleted.`);
      setDeleteTarget(null);
      await loadTerritories();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to delete market.';
      showSnack(detail, 'error');
    } finally {
      setDeleteSaving(false);
    }
  };

  // ─── Withdraw Submission ───────────────────────────────────────────

  const handleCancelSubmissionOpen = (submissionId) => {
    setCancelSubmissionId(submissionId);
    setCancelDialogOpen(true);
  };

  const handleCancelSubmissionConfirm = async () => {
    setCancelSaving(true);
    try {
      await cancelZipSubmission(cancelSubmissionId);
      setCancelDialogOpen(false);
      showSnack('Submission withdrawn.');
      loadSubmissions();
    } catch {
      showSnack('Failed to withdraw submission.', 'error');
    } finally {
      setCancelSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────

  const showClientSelector = isStaff || memberships.length > 1 || isParentScopeMember;

  return (
    <Box p={3}>
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" fontWeight={700}>ZIP Coverage</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            ref={walkthroughRefs.newTerritoryBtn}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewTerritoryName('');
              setNewTerritoryError('');
              setNewTerritoryDialogOpen(true);
            }}
            disabled={!selectedClientId}
          >
            New Territory
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleOpenBulkUpload}
            disabled={!hasScope}
          >
            Bulk Upload
          </Button>
          <IconButton
            size="small"
            onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={() => setMoreMenuAnchor(null)}
          >
            <MuiMenuItem
              ref={walkthroughRefs.addVerticalBtn}
              onClick={() => { setAddVerticalDialogOpen(true); setMoreMenuAnchor(null); }}
              disabled={!selectedClientId}
            >
              <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add Vertical
            </MuiMenuItem>
            <MuiMenuItem
              onClick={() => {
                loadTerritories(); loadVerticals(); loadZips(); loadSubmissions();
                setMoreMenuAnchor(null);
              }}
              disabled={loading || !hasScope}
            >
              <RefreshIcon fontSize="small" sx={{ mr: 1 }} /> Refresh
            </MuiMenuItem>
            <MuiMenuItem
              onClick={() => { setWalkthroughStep(0); setWalkthroughActive(true); setMoreMenuAnchor(null); }}
            >
              <HelpOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Take a Tour
            </MuiMenuItem>
          </Menu>
        </Stack>
      </Box>

      {showClientSelector && (
        <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
          {(isStaff || isParentScopeMember) && parentCompanyOptions.length > 0 && (
            <Autocomplete
              options={parentCompanyOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={findParentCompanyByValue(selectedParentCompanyId)}
              onChange={(_, val) => {
                setSelectedParentCompanyId(val ? String(val.value) : '');
                setSelectedClientId('');
              }}
              disabled={isParentScopeMember}
              renderInput={(params) => (
                <TextField {...params} label="Parent Company" size="small" sx={{ minWidth: 240 }} />
              )}
              sx={{ minWidth: 240 }}
            />
          )}
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={findOptionByValue(selectedClientId)}
            onChange={(_, val) => setSelectedClientId(val ? String(val.value) : '')}
            onInputChange={handleClientSearch}
            renderInput={(params) => (
              <TextField {...params} label="Client" size="small" sx={{ minWidth: 280 }} />
            )}
            sx={{ minWidth: 280 }}
          />
        </Stack>
      )}

      {!hasScope && (
        <Alert severity="info">Select a client to view ZIP coverage.</Alert>
      )}

      {hasScope && loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* ─── Quick Stats ──────────────────────────────────────────────── */}
      {hasScope && !loading && (
        <Box ref={walkthroughRefs.statsBar} display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          {isAllClientsView && (
            <StatCard label="Clients" value={clientGroups.length} />
          )}
          <StatCard label="Territories" value={stats.territories} />
          <StatCard
            label="Active ZIPs"
            value={stats.standardZips.toLocaleString()}
            subtitle={
              stats.totalZips !== stats.standardZips
                ? `+ ${(stats.totalZips - stats.standardZips).toLocaleString()} PO Box/Unique (can't be targeted on Meta)`
                : null
            }
          />
          {stats.standardPopulation > 0 && (
            <StatCard
              label="Population"
              value={stats.standardPopulation.toLocaleString()}
              subtitle={
                stats.totalPopulation !== stats.standardPopulation
                  ? `from targetable ZIPs only`
                  : null
              }
            />
          )}
          <StatCard label="Verticals" value={stats.verticals} />
          <StatCard
            label="Pending"
            value={stats.pending}
            highlight={stats.pending > 0}
          />
          {stats.totalZips > 0 && selectedClientId && (
            <Tooltip title="Download ZIP coverage as CSV">
              <IconButton
                size="small"
                onClick={async () => {
                  try {
                    const res = await exportZipCoverageCsv(selectedClientId);
                    const blob = new Blob([res.data], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `zip_coverage_${selectedClientId}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    showSnack('CSV downloaded.');
                  } catch {
                    showSnack('Failed to download CSV.', 'error');
                  }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* ─── ZIP Search ───────────────────────────────────────────────── */}
      {hasScope && !loading && stats.totalZips > 0 && (
        <Box ref={walkthroughRefs.zipSearch} mb={2}>
          <TextField
            size="small"
            placeholder="Search for a ZIP code..."
            value={zipSearchText}
            onChange={(e) => setZipSearchText(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          {zipSearchResults !== null && (
            <Paper variant="outlined" sx={{ mt: 1, p: 1.5, maxWidth: 500 }}>
              {zipSearchResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No matching ZIPs found.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {zipSearchResults.map((r, i) => (
                    <Box key={i}>
                      <Typography variant="body2" fontWeight={600}>
                        {isAllClientsView && r.client ? `${r.client} · ` : ''}{r.territory} / {r.vertical}
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {r.zips.slice(0, 20).map((z) => (
                          <Chip key={z} label={z} size="small" variant="outlined" />
                        ))}
                        {r.zips.length > 20 && (
                          <Chip label={`+${r.zips.length - 20} more`} size="small" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* ─── Pending Submissions Banner (visible on all tabs) ──────────── */}
      {hasScope && !loading && submissions.length > 0 && (
        <Alert
          severity="warning"
          icon={<PendingActionsIcon fontSize="inherit" />}
          sx={{ mb: 2, alignItems: 'center' }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="warning"
                component={RouterLink}
                to={selectedClientId
                  ? `${paths.zipSubmissions}?client_id=${selectedClientId}&status=pending`
                  : `${paths.zipSubmissions}?status=pending`}
              >
                Review
              </Button>
            </Stack>
          }
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
            {submissions.length} ZIP change request{submissions.length > 1 ? 's' : ''} in progress — {autoReviewOn ? 'most apply automatically within minutes' : "awaiting our team's review"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {submissions
              .slice(0, 3)
              .map((sub) => {
                const t = territories.find((x) => x.id === sub.client_territory)?.name
                  || sub.territory_name
                  || 'Unknown territory';
                const v = sub.vertical_name || verticalById[sub.vertical]?.vertical || 'Vertical';
                const adds = (sub.items || []).filter((i) => i.action === 'add').length;
                const removes = (sub.items || []).filter((i) => i.action === 'remove').length;
                const delta = [adds ? `+${adds}` : null, removes ? `−${removes}` : null]
                  .filter(Boolean)
                  .join(' / ');
                const c = isAllClientsView && sub.client_name ? `${sub.client_name}: ` : '';
                return `${c}${t} · ${v}${delta ? ` (${delta} ZIPs)` : ''}`;
              })
              .join(' · ')}
            {submissions.length > 3 ? ` · +${submissions.length - 3} more` : ''}
          </Typography>
        </Alert>
      )}

      {/* ─── Tabs ─────────────────────────────────────────────────────── */}
      {hasScope && !loading && (
        <Tabs
          ref={walkthroughRefs.historyTab}
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Coverage" />
          <Tab label="Map" />
          <Tab label="History" />
        </Tabs>
      )}

      {/* ═══════════ TAB 0: Coverage ═══════════ */}
      {selectedClientId && !loading && activeTab === 0 && (
        <>
          {/* Guided empty state */}
          {territories.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 5,
                textAlign: 'center',
                mt: 2,
                borderStyle: 'dashed',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <MapIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Set up your ZIP coverage
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
                Define the territories and ZIP codes where you want to receive leads.
                {autoReviewOn
                  ? ` Most changes apply automatically — additions go live right away, removals wait ${undoWindowMin} minutes so you can undo them, and only large coverage reductions are reviewed by our team.`
                  : ' All changes go through a quick review before going live.'}
              </Typography>
              <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420, mx: 'auto', mb: 3 }}>
                <StepRow number={1} text='Click "New Territory" above to create your first territory' />
                <StepRow number={2} text="Select which verticals (service types) you offer" />
                <StepRow number={3} text="Add your ZIP codes — paste them in or upload a CSV" />
              </Stack>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewTerritoryName('');
                  setNewTerritoryError('');
                  setNewTerritoryDialogOpen(true);
                }}
              >
                Create Your First Territory
              </Button>
            </Paper>
          )}


          {/* Rejections are intentionally NOT surfaced on this page. In practice a
              staff "reject" almost always just means the client submitted ZIPs
              incorrectly and should resubmit — a red banner here read as a hard
              judgment and confused clients. The outcome still lives in the
              Submission History tab and the review email; the expected path is
              "withdraw and resubmit," not a rejection flag. */}

          {/* Expand/Collapse all — only when there are enough territories to matter */}
          {territories.length > 3 && (
            <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
              <Button
                size="small"
                variant="text"
                onClick={() => setExpandedTerritoryIds(new Set(territories.map((t) => t.id)))}
              >
                Expand all
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => setExpandedTerritoryIds(new Set())}
              >
                Collapse all
              </Button>
            </Box>
          )}

          {/* Territory Cards */}
          {territories.map((territory, tIdx) => {
            const tVerticalIds = territoryVerticals[territory.id] || new Set();
            const pendingVerticalIds = new Set();
            submissions
              .filter((s) => s.client_territory === territory.id)
              .forEach((s) => pendingVerticalIds.add(s.vertical));
            const usedVerticalIds = new Set([...tVerticalIds, ...pendingVerticalIds]);
            const displayVerticals = [...usedVerticalIds]
              .map((vId) => verticalById[vId])
              .filter(Boolean);
            const hasPending = territoriesWithPending.has(territory.id);
            const territoryKeys = Object.keys(zipsByTerritoryVertical)
              .filter((k) => k.startsWith(`${territory.id}__`));
            const territoryZipCount = territoryKeys
              .reduce((sum, k) => sum + zipsByTerritoryVertical[k].length, 0);
            const territoryStandardZipCount = territoryKeys
              .reduce(
                (sum, k) => sum + zipsByTerritoryVertical[k].filter(
                  (z) => (z.zip_type || '').toUpperCase() === 'STANDARD',
                ).length,
                0,
              );
            const territoryPopulation = territoryKeys
              .reduce((sum, k) => sum + zipsByTerritoryVertical[k].reduce((s, z) => s + (z.population || 0), 0), 0);

            const TERRITORY_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1', '#d32f2f', '#689f38', '#5d4037'];
            // Empty = no active ZIPs at all. Paused = has ZIPs but they're held
            // out of Meta targeting (territory.is_paused from the API). The two
            // states look different so a paused market is unmistakably "off".
            const isEmpty = territoryZipCount === 0 && !hasPending;
            const isMarketPaused = Boolean(territory.is_paused) && !hasPending;
            const isDimmed = isEmpty || isMarketPaused;
            const borderColor = hasPending
              ? '#ed6c02'
              : isMarketPaused
                ? '#ed6c02'
                : isEmpty
                  ? '#9e9e9e'
                  : TERRITORY_COLORS[tIdx % TERRITORY_COLORS.length];

            const defaultExpanded = territories.length <= 10 && !isDimmed;
            const expanded = expandedTerritoryIds == null
              ? defaultExpanded
              : expandedTerritoryIds.has(territory.id);

            return (
              <Accordion
                key={territory.id}
                ref={tIdx === 0 ? walkthroughRefs.territoryCard : undefined}
                expanded={expanded}
                onChange={(_, isExpanded) => {
                  setExpandedTerritoryIds((prev) => {
                    // Initialize from current default state on first interaction
                    const base = prev == null
                      ? new Set(
                        territories
                          .filter((t) => {
                            const tKeys = Object.keys(zipsByTerritoryVertical)
                              .filter((k) => k.startsWith(`${t.id}__`));
                            const tCount = tKeys
                              .reduce((sum, k) => sum + zipsByTerritoryVertical[k].length, 0);
                            const tDimmed = (tCount === 0 || t.is_paused)
                              && !territoriesWithPending.has(t.id);
                            return territories.length <= 10 && !tDimmed;
                          })
                          .map((t) => t.id),
                      )
                      : new Set(prev);
                    if (isExpanded) base.add(territory.id);
                    else base.delete(territory.id);
                    return base;
                  });
                }}
                sx={{
                  borderLeft: `4px solid ${borderColor}`,
                  '&:before': { display: 'none' },
                  ...(isDimmed && { opacity: 0.7 }),
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Typography variant="h6" fontWeight={600} color={isDimmed ? 'text.secondary' : 'text.primary'}>
                      {territory.name}
                    </Typography>
                    {isMarketPaused && (
                      <Tooltip title={`Paused${territory.paused_at ? ` since ${new Date(territory.paused_at).toLocaleString()}` : ''}: held out of Meta targeting so the market isn't spending. Its ZIPs and historical spend are kept — nothing is lost. Click for pause history; click resume to relaunch.`}>
                        <Chip
                          icon={<PauseCircleOutlineIcon sx={{ fontSize: 16 }} />}
                          label={territory.paused_at
                            ? `Paused ${formatDistanceToNow(new Date(territory.paused_at), { addSuffix: true })}`
                            : 'Paused'}
                          size="small"
                          color="warning"
                          variant="filled"
                          sx={{ fontWeight: 600 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenPauseHistory(territory);
                          }}
                        />
                      </Tooltip>
                    )}
                    {isEmpty && (
                      <Chip
                        icon={<PauseCircleOutlineIcon sx={{ fontSize: 16 }} />}
                        label="No Active ZIPs"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {!isEmpty && displayVerticals.length > 0 && (
                      <Chip
                        label={`${displayVerticals.length} vertical${displayVerticals.length > 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {territoryZipCount > 0 && (
                      <Chip
                        label={`${territoryStandardZipCount.toLocaleString()} ZIPs`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                    {territoryStandardZipCount !== territoryZipCount && (
                      <Tooltip title="PO Box and Unique ZIPs can't be targeted on Meta. They're excluded from the active count.">
                        <Chip
                          label={`+${(territoryZipCount - territoryStandardZipCount).toLocaleString()} non-targetable`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {territoryPopulation > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {territoryPopulation.toLocaleString()} pop.
                      </Typography>
                    )}
                    {hasPending && (() => {
                      const tSubs = submissions.filter((s) => s.client_territory === territory.id);
                      let pAdds = 0;
                      let pRemoves = 0;
                      tSubs.forEach((s) => (s.items || []).forEach((i) => {
                        if (i.action === 'add') pAdds += 1;
                        else if (i.action === 'remove') pRemoves += 1;
                      }));
                      const delta = [pAdds ? `+${pAdds}` : null, pRemoves ? `−${pRemoves}` : null]
                        .filter(Boolean).join(' / ');
                      const newest = tSubs[0];
                      const when = newest?.submitted_at
                        ? formatDistanceToNow(new Date(newest.submitted_at), { addSuffix: true })
                        : '';
                      // Auto-review presentation: strongest state across the
                      // territory's pending batches (countdown > imminent >
                      // hold); null tiers keep the generic pending wording.
                      let chipLabel = delta ? `${delta} pending review` : 'pending review';
                      let chipColor = 'warning';
                      let chipTip = `ZIP changes awaiting our review${when ? ` — submitted ${when}` : ''}. Click to view or withdraw.`;
                      if (autoReviewOn) {
                        const states = tSubs.map((s) => pendingAutoState(s, nowTs)).filter(Boolean);
                        const countdowns = states.filter((st) => st.kind === 'countdown');
                        if (countdowns.length) {
                          const mins = Math.max(...countdowns.map((st) => st.minutesLeft));
                          chipLabel = delta ? `${delta} applying in ~${mins} min` : `applying in ~${mins} min`;
                          chipColor = 'info';
                          chipTip = 'These ZIP changes apply automatically — you can undo them until the timer runs out. Click to view or undo.';
                        } else if (states.length && states.every((st) => st.kind === 'imminent')) {
                          chipLabel = delta ? `${delta} applying now` : 'applying now';
                          chipColor = 'info';
                          chipTip = 'These ZIP changes are being applied and pushed to your campaigns.';
                        } else if (states.length && states.every((st) => st.kind === 'hold')) {
                          chipLabel = delta ? `${delta} under review` : 'under review';
                          chipTip = `These changes reduce your coverage significantly, so our team reviews them first${when ? ` — submitted ${when}` : ''}.`;
                        }
                      }
                      return (
                        <Tooltip title={chipTip}>
                          <Chip
                            label={chipLabel}
                            size="small"
                            color={chipColor}
                            component={RouterLink}
                            to={selectedClientId
                              ? `${paths.zipSubmissions}?client_id=${selectedClientId}&status=pending`
                              : `${paths.zipSubmissions}?status=pending`}
                            clickable
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Tooltip>
                      );
                    })()}
                    {(() => {
                      const meta = latestByTerritoryMap[territory.id];
                      if (!meta) return null;
                      const ago = formatDistanceToNow(meta.when, { addSuffix: true });
                      const by = meta.reviewedBy || meta.submittedBy;
                      return (
                        <Tooltip title={`Last approved ${meta.when.toLocaleString()}${by ? ` by ${by}` : ''}`}>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            Updated {ago}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                    <Box flexGrow={1} />
                    {(() => {
                      // Show territory-level Manage ZIPs when:
                      //  • single vertical (always)
                      //  • multi-vertical AND every vertical shares the
                      //    same ZIP set (the 95% case — edit all at once)
                      if (displayVerticals.length === 0) return null;
                      const sigs = new Set(
                        displayVerticals.map((v) => {
                          const zips = zipsByTerritoryVertical[`${territory.id}__${v.id}`] || [];
                          return zips.map((z) => z.zip).sort().join(',');
                        }),
                      );
                      const inSync = sigs.size === 1;
                      if (displayVerticals.length > 1 && !inSync) return null;
                      const target = displayVerticals.length === 1
                        ? displayVerticals[0]
                        : displayVerticals;
                      return (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenManageDialog(territory, target);
                          }}
                        >
                          Manage ZIPs
                          {displayVerticals.length > 1 ? ` (${displayVerticals.length} verticals)` : ''}
                        </Button>
                      );
                    })()}
                    <Tooltip title="Rename territory">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenRename(territory);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Pause / resume history">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenPauseHistory(territory);
                        }}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!isEmpty && (
                      <Tooltip title={isMarketPaused
                        ? 'Resume — restart your ads in this territory'
                        : 'Pause — temporarily stop your ads here (keeps all ZIPs & history)'}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color={isMarketPaused ? 'warning' : 'inherit'}
                            disabled={pauseTogglingId === territory.id}
                            startIcon={pauseTogglingId === territory.id
                              ? <CircularProgress size={14} />
                              : isMarketPaused
                                ? <PlayCircleOutlineIcon fontSize="small" />
                                : <PauseCircleOutlineIcon fontSize="small" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isMarketPaused) {
                                handleTogglePause(territory, false);
                              } else {
                                setPauseConfirmTerritory(territory);
                              }
                            }}
                            sx={{ minWidth: 0, whiteSpace: 'nowrap' }}
                          >
                            {isMarketPaused ? 'Resume' : 'Pause'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {isStaff && (
                      <Tooltip title="Delete this market (staff only) — removes its ZIPs entirely">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(territory);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {displayVerticals.length === 0 && (
                    <Box textAlign="center" py={3}>
                      <PlaceIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
                      <Typography variant="body1" fontWeight={500} gutterBottom>
                        No ZIPs in this territory yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select a vertical below, then add your ZIP codes.
                      </Typography>
                    </Box>
                  )}

                  {/* Add ZIPs for a vertical not yet in this territory */}
                  {(() => {
                    const unusedVerticals = availableVerticals.filter((v) => !usedVerticalIds.has(v.id));
                    if (unusedVerticals.length === 0) return null;
                    return (
                      <Box mb={2}>
                        <Autocomplete
                          options={unusedVerticals}
                          getOptionLabel={(v) => v.vertical}
                          onChange={(_, val) => {
                            if (val) handleOpenManageDialog(territory, val);
                          }}
                          value={null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Add ZIPs for a vertical..."
                              size="small"
                              sx={{ maxWidth: 300 }}
                            />
                          )}
                          blurOnSelect
                          clearOnBlur
                        />
                      </Box>
                    );
                  })()}

                  {(() => {
                    // Group verticals by identical ZIP sets so we render one row
                    // per unique coverage instead of N identical rows.
                    const groupsMap = new Map();
                    displayVerticals.forEach((v) => {
                      const key = `${territory.id}__${v.id}`;
                      const zips = zipsByTerritoryVertical[key] || [];
                      const sig = zips
                        .map((z) => z.zip)
                        .sort()
                        .join(',');
                      if (!groupsMap.has(sig)) {
                        groupsMap.set(sig, { verticals: [], zips });
                      }
                      groupsMap.get(sig).verticals.push(v);
                    });
                    const verticalGroups = [...groupsMap.values()];

                    return verticalGroups.map((group) => {
                      const zips = group.zips;
                      const verticals = group.verticals;
                      const isConsolidated = verticals.length > 1;
                      const groupKey = verticals.map((v) => v.id).sort().join('-');
                      const pendingSubs = verticals.flatMap(
                        (v) => pendingByTerritoryVertical[`${territory.id}__${v.id}`] || [],
                      );

                      return (
                      <Paper key={groupKey} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                            {isConsolidated ? (
                              <>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {verticals.length} verticals
                                </Typography>
                                <Tooltip title="These verticals share an identical ZIP set. Click a vertical to edit it on its own.">
                                  <Stack direction="row" spacing={0.5}>
                                    {verticals.map((v) => (
                                      <Chip
                                        key={v.id}
                                        label={v.vertical}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleOpenManageDialog(territory, v)}
                                        sx={{ cursor: 'pointer' }}
                                      />
                                    ))}
                                  </Stack>
                                </Tooltip>
                              </>
                            ) : (
                              <Typography variant="subtitle1" fontWeight={600}>
                                {verticals[0].vertical}
                              </Typography>
                            )}
                            {(() => {
                              const standardCount = zips.filter(
                                (z) => (z.zip_type || '').toUpperCase() === 'STANDARD',
                              ).length;
                              const excluded = zips.length - standardCount;
                              const standardPop = zips.reduce(
                                (s, z) => s + ((z.zip_type || '').toUpperCase() === 'STANDARD' ? (z.population || 0) : 0),
                                0,
                              );
                              return (
                                <>
                                  <Chip
                                    label={`${standardCount.toLocaleString()} active`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                  {standardPop > 0 && (
                                    <Chip
                                      label={`${standardPop.toLocaleString()} pop.`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  )}
                                  {excluded > 0 && (
                                    <Tooltip title="PO Box and Unique ZIPs can't be targeted on Meta. They're excluded from the active count.">
                                      <Chip
                                        label={`+${excluded.toLocaleString()} non-targetable`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                      />
                                    </Tooltip>
                                  )}
                                </>
                              );
                            })()}
                            {pendingSubs.length > 0 && (
                              <Chip
                                label={`${pendingSubs.length} pending`}
                                size="small"
                                color="warning"
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                // Consolidated rows share one ZIP set across
                                // multiple verticals — edit them all in one
                                // submission. User can opt into per-vertical
                                // editing from inside the dialog.
                                handleOpenManageDialog(
                                  territory,
                                  isConsolidated ? verticals : verticals[0],
                                );
                              }}
                            >
                              Manage ZIPs
                            </Button>
                            <Tooltip title={isConsolidated ? 'Pause a vertical' : 'Pause this vertical'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  if (isConsolidated) {
                                    setGroupManageMenu({
                                      anchorEl: e.currentTarget,
                                      territory,
                                      verticals,
                                      action: 'pause',
                                    });
                                  } else {
                                    handleTogglePause(territory, true, verticals[0]);
                                  }
                                }}
                              >
                                <PauseCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        {/* County roll-up — counties hidden by default; click to reveal */}
                        {zips.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No active ZIPs
                          </Typography>
                        ) : (
                          (() => {
                            const byCounty = new Map();
                            zips.forEach((czv) => {
                              const county = czv.county || '—';
                              const state = czv.state || '';
                              const gkey = `${county}|${state}`;
                              if (!byCounty.has(gkey)) {
                                byCounty.set(gkey, { county, state, zips: [] });
                              }
                              byCounty.get(gkey).zips.push(czv);
                            });
                            const groups = [...byCounty.values()].sort((a, b) => {
                              if (b.zips.length !== a.zips.length) return b.zips.length - a.zips.length;
                              return a.county.localeCompare(b.county);
                            });
                            const vKey = `${territory.id}__group${groupKey}`;
                            const countiesOpen = expandedVerticalCountiesKeys.has(vKey);
                            const stateSummary = (() => {
                              const states = new Set(
                                groups.map((g) => g.state).filter(Boolean),
                              );
                              if (states.size === 0) return '';
                              if (states.size === 1) return ` · ${[...states][0]}`;
                              return ` · ${states.size} states`;
                            })();
                            return (
                              <>
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={
                                    <ExpandMoreIcon
                                      sx={{
                                        transform: countiesOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                        transition: 'transform 150ms',
                                      }}
                                    />
                                  }
                                  onClick={() => {
                                    setExpandedVerticalCountiesKeys((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(vKey)) next.delete(vKey);
                                      else next.add(vKey);
                                      return next;
                                    });
                                  }}
                                  sx={{ textTransform: 'none', px: 0.5, mb: 0.5, color: 'text.secondary' }}
                                >
                                  {countiesOpen ? 'Hide counties' : `Show ${groups.length} ${groups.length === 1 ? 'county' : 'counties'}${stateSummary}`}
                                </Button>
                                {countiesOpen && (
                                  <Stack spacing={0.5}>
                                {groups.map((g) => {
                                  const ek = `${territory.id}__group${groupKey}__${g.county}|${g.state}`;
                                  const isOpen = expandedCountyKeys.has(ek);
                                  const pop = g.zips.reduce((s, z) => s + (z.population || 0), 0);
                                  const standardCount = g.zips.filter(
                                    (z) => (z.zip_type || '').toUpperCase() === 'STANDARD',
                                  ).length;
                                  return (
                                    <Box key={ek}>
                                      <Box
                                        onClick={() => {
                                          setExpandedCountyKeys((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(ek)) next.delete(ek);
                                            else next.add(ek);
                                            return next;
                                          });
                                        }}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 1,
                                          cursor: 'pointer',
                                          '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                      >
                                        <ExpandMoreIcon
                                          sx={{
                                            fontSize: 18,
                                            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                            transition: 'transform 150ms',
                                            color: 'action.active',
                                          }}
                                        />
                                        <Typography variant="body2" fontWeight={600}>
                                          {g.county}{g.state ? `, ${g.state}` : ''}
                                        </Typography>
                                        <Chip
                                          label={`${g.zips.length} ZIP${g.zips.length === 1 ? '' : 's'}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                        {standardCount !== g.zips.length && (
                                          <Tooltip title="PO Box / Unique ZIPs in this county can't be targeted on Meta.">
                                            <Chip
                                              label={`+${(g.zips.length - standardCount).toLocaleString()} non-targetable`}
                                              size="small"
                                              color="warning"
                                              variant="outlined"
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                          </Tooltip>
                                        )}
                                        {pop > 0 && (
                                          <Typography variant="caption" color="text.secondary">
                                            {pop.toLocaleString()} pop.
                                          </Typography>
                                        )}
                                      </Box>
                                      {isOpen && (
                                        <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ pl: 3.5, pb: 0.75 }}>
                                          {g.zips.map((czv) => (
                                            <Tooltip
                                              key={czv.id}
                                              title={czv.city && czv.state ? `${czv.city}, ${czv.state}` : ''}
                                              arrow
                                            >
                                              <Chip
                                                label={czv.zip}
                                                size="small"
                                                variant="outlined"
                                                color={(czv.zip_type || '').toUpperCase() === 'STANDARD' ? 'default' : 'warning'}
                                              />
                                            </Tooltip>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                                  </Stack>
                                )}
                              </>
                            );
                          })()
                        )}

                        {/* Pending submissions for this territory+vertical */}
                        {pendingSubs.map((sub) => {
                          const adds = (sub.items || []).filter((i) => i.action === 'add');
                          const removes = (sub.items || []).filter((i) => i.action === 'remove');
                          const auto = autoReviewOn ? pendingAutoState(sub, nowTs) : null;
                          const headline = auto?.kind === 'countdown'
                            ? `Applying in ~${auto.minutesLeft} min — you can undo until then`
                            : auto?.kind === 'imminent'
                              ? 'Applying now — pushing to your campaigns'
                              : auto?.kind === 'hold'
                                ? `Under review by our team — submitted ${new Date(sub.submitted_at).toLocaleDateString()}`
                                : `Pending — submitted ${new Date(sub.submitted_at).toLocaleDateString()}`;
                          return (
                            <Alert
                              key={sub.id}
                              severity={auto && auto.kind !== 'hold' ? 'info' : 'warning'}
                              sx={{ mt: 1 }}
                              action={
                                <Button
                                  size="small"
                                  color="inherit"
                                  onClick={() => handleCancelSubmissionOpen(sub.id)}
                                >
                                  {auto?.kind === 'countdown' ? 'Undo' : 'Withdraw'}
                                </Button>
                              }
                            >
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                {isConsolidated && sub.vertical_name ? `${sub.vertical_name} — ` : ''}
                                {headline}
                              </Typography>
                              {adds.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mb={0.5}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                    Adding:
                                  </Typography>
                                  {adds.slice(0, 30).map((item) => (
                                    <Chip
                                      key={item.id}
                                      label={item.zip || item.zip_raw}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                  {adds.length > 30 && (
                                    <Chip label={`+${adds.length - 30} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                              )}
                              {removes.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                    Removing:
                                  </Typography>
                                  {removes.slice(0, 30).map((item) => (
                                    <Chip
                                      key={item.id}
                                      label={item.zip || item.zip_raw}
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                  {removes.length > 30 && (
                                    <Chip label={`+${removes.length - 30} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                              )}
                            </Alert>
                          );
                        })}
                      </Paper>
                      );
                    });
                  })()}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Shared menu for consolidated verticals — Manage or Pause */}
          <Menu
            open={Boolean(groupManageMenu)}
            anchorEl={groupManageMenu?.anchorEl}
            onClose={() => setGroupManageMenu(null)}
          >
            {(groupManageMenu?.verticals || []).map((v) => (
              <MenuItem
                key={v.id}
                onClick={() => {
                  const { territory, action } = groupManageMenu;
                  setGroupManageMenu(null);
                  if (action === 'manage') handleOpenManageDialog(territory, v);
                  else if (action === 'pause') handleTogglePause(territory, true, v);
                }}
              >
                {groupManageMenu?.action === 'pause' ? `Pause ${v.vertical}` : v.vertical}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* ═══════════ TAB 0 (parent all-clients): Coverage overview ═══════════ */}
      {isAllClientsView && !loading && activeTab === 0 && (
        <>
          {clientGroups.length === 0 && (
            <Alert severity="info">
              No territories found for this parent company yet. Pick a client above to set up coverage.
            </Alert>
          )}
          {clientGroups.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Showing all clients under this parent company. Select a client above (or click a client below) to manage its territories and ZIPs.
            </Alert>
          )}
          <Stack spacing={2}>
            {clientGroups.map((group) => {
              const groupTerritoryIds = group.territories.map((t) => t.id);
              const groupKeys = Object.keys(zipsByTerritoryVertical).filter((k) =>
                groupTerritoryIds.some((tid) => k.startsWith(`${tid}__`))
              );
              const groupStandardZips = groupKeys.reduce(
                (sum, k) => sum + zipsByTerritoryVertical[k].filter(
                  (z) => (z.zip_type || '').toUpperCase() === 'STANDARD'
                ).length,
                0,
              );
              const groupPendingCount = submissions.filter(
                (s) => s.client === group.clientId
              ).length;
              return (
                <Paper key={group.clientId} variant="outlined">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Typography variant="h6" fontWeight={600}>
                        {group.clientName}
                      </Typography>
                      <Chip
                        size="small"
                        icon={<PlaceIcon />}
                        label={`${group.territories.length} territor${group.territories.length === 1 ? 'y' : 'ies'}`}
                      />
                      <Chip size="small" label={`${groupStandardZips.toLocaleString()} active ZIPs`} />
                      {groupPendingCount > 0 && (
                        <Chip
                          size="small"
                          color="warning"
                          icon={<PendingActionsIcon />}
                          label={`${groupPendingCount} pending`}
                        />
                      )}
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedClientId(String(group.clientId))}
                    >
                      View &amp; Manage
                    </Button>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Territory</TableCell>
                        <TableCell>Verticals</TableCell>
                        <TableCell align="right">Active ZIPs</TableCell>
                        <TableCell align="right">Population</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.territories.map((territory) => {
                        const territoryKeys = Object.keys(zipsByTerritoryVertical)
                          .filter((k) => k.startsWith(`${territory.id}__`));
                        const standardZipCount = territoryKeys.reduce(
                          (sum, k) => sum + zipsByTerritoryVertical[k].filter(
                            (z) => (z.zip_type || '').toUpperCase() === 'STANDARD'
                          ).length,
                          0,
                        );
                        const population = territoryKeys.reduce(
                          (sum, k) => sum + zipsByTerritoryVertical[k].reduce(
                            (s, z) => s + (z.population || 0), 0
                          ),
                          0,
                        );
                        const verticalNames = [...(territoryVerticals[territory.id] || new Set())]
                          .map((vId) => verticalById[vId]?.vertical)
                          .filter(Boolean)
                          .sort();
                        const hasPending = territoriesWithPending.has(territory.id);
                        const isMarketPaused = Boolean(territory.is_paused);
                        const isEmpty = standardZipCount === 0 && !hasPending;
                        const last = latestByTerritoryMap[territory.id];
                        return (
                          <TableRow key={territory.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {territory.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {verticalNames.join(', ') || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{standardZipCount.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              {population > 0 ? population.toLocaleString() : '—'}
                            </TableCell>
                            <TableCell>
                              {hasPending ? (
                                <Chip size="small" color="warning" label="Pending changes" />
                              ) : isMarketPaused ? (
                                <Chip size="small" color="warning" variant="outlined" label="Paused" />
                              ) : isEmpty ? (
                                <Chip size="small" label="Empty" />
                              ) : (
                                <Chip size="small" color="success" variant="outlined" label="Active" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {last?.when
                                  ? formatDistanceToNow(last.when, { addSuffix: true })
                                  : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              );
            })}
          </Stack>
        </>
      )}

      {/* ═══════════ TAB 1: Map ═══════════ */}
      {hasScope && !loading && activeTab === 1 && (
        <Box>
          <ClientZipMap
            clientId={selectedClientId || undefined}
            parentCompanyId={isAllClientsView ? selectedParentCompanyId : undefined}
            skipLeads
          />
        </Box>
      )}

      {/* ═══════════ TAB 2: History ═══════════ */}
      {hasScope && !loading && activeTab === 2 && (
        <Box>
          {/* History filters */}
          <Box display="flex" gap={2} mb={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={historyFilter}
                label="Status"
                onChange={(e) => setHistoryFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Pause / resume activity — instant toggles that never create a
              submission, so they'd otherwise be invisible in this log. */}
          {pauseEvents.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Pause / Resume activity
              </Typography>
              <Stack spacing={0.75}>
                {pauseEvents.map((event) => (
                  <Box key={event.id} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      icon={event.paused
                        ? <PauseCircleOutlineIcon sx={{ fontSize: 14 }} />
                        : <PlayCircleOutlineIcon sx={{ fontSize: 14 }} />}
                      label={event.paused ? 'Paused' : 'Resumed'}
                      color={event.paused ? 'warning' : 'success'}
                      variant="outlined"
                      sx={{ fontWeight: 600, height: 22 }}
                    />
                    <Typography variant="body2">
                      {event.territory_name}
                      {event.vertical ? ` / ${event.vertical}` : ''}
                      {' '}({event.zip_count} ZIP{event.zip_count !== 1 ? 's' : ''})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(event.created_at).toLocaleDateString()}{' '}
                      {new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {event.actor ? ` — ${event.actor}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {historyLoading && historySubmissions.length === 0 && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )}

          {!historyLoading && historySubmissions.length === 0 && (
            <Alert severity="info">No submission history found.</Alert>
          )}

          {historySubmissions.length > 0 && (
            <Stack spacing={1.5}>
              {historySubmissions.map((sub) => {
                const cfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
                // Pending rows under auto-review get a truthful chip instead
                // of the generic "Pending" (they auto-apply unless held).
                const auto = autoReviewOn ? pendingAutoState(sub, nowTs) : null;
                const statusLabel = auto?.kind === 'countdown'
                  ? `Applying in ~${auto.minutesLeft} min`
                  : auto?.kind === 'imminent'
                    ? 'Applying'
                    : auto?.kind === 'hold'
                      ? 'Under review'
                      : cfg.label;
                const statusColor = auto ? (auto.kind === 'hold' ? 'warning' : 'info') : cfg.color;
                const adds = (sub.items || []).filter((i) => i.action === 'add');
                const removes = (sub.items || []).filter((i) => i.action === 'remove');
                return (
                  <Paper key={sub.id} variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          {isAllClientsView && sub.client_name ? `${sub.client_name} — ` : ''}
                          {sub.territory_name || 'Unknown Territory'} / {sub.vertical_name || 'Unknown'}
                        </Typography>
                        <Chip label={statusLabel} size="small" color={statusColor} />
                        {sub.reverted && (
                          <Tooltip title="A revert of this change has been submitted or applied.">
                            <Chip label="Reverted" size="small" color="default" variant="outlined" />
                          </Tooltip>
                        )}
                        {sub.reverted_from && (
                          <Tooltip title={`This change undoes submission #${sub.reverted_from}.`}>
                            <Chip label={`Revert of #${sub.reverted_from}`} size="small" color="info" variant="outlined" />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(sub.submitted_at).toLocaleDateString()}{' '}
                        {new Date(sub.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} mb={1}>
                      {sub.add_count > 0 && (
                        <Typography variant="body2" color="success.main">
                          +{sub.add_count} add{sub.add_count !== 1 ? 's' : ''}
                        </Typography>
                      )}
                      {sub.remove_count > 0 && (
                        <Typography variant="body2" color="error.main">
                          -{sub.remove_count} removal{sub.remove_count !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Stack>

                    {adds.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                        {adds.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {adds.length > 20 && (
                          <Chip label={`+${adds.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}
                    {removes.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                        {removes.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {removes.length > 20 && (
                          <Chip label={`+${removes.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}

                    {sub.reviewer_notes && (
                      <Alert severity={sub.status === 'rejected' ? 'error' : 'info'} sx={{ mt: 1 }} variant="outlined">
                        <Typography variant="body2">
                          <strong>Reviewer note:</strong> {sub.reviewer_notes}
                        </Typography>
                      </Alert>
                    )}

                    {sub.reviewed_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {sub.reviewed_by_email
                          ? `Reviewed ${new Date(sub.reviewed_at).toLocaleDateString()} by ${sub.reviewed_by_email}`
                          : `Applied automatically ${new Date(sub.reviewed_at).toLocaleDateString()}`}
                      </Typography>
                    )}

                    <Box mt={1} display="flex" gap={1}>
                      {(() => {
                        const hasGeo = (sub.items || []).some(
                          (i) => i.latitude != null && i.longitude != null,
                        );
                        if (!hasGeo) return null;
                        const open = historyMapOpenIds.has(sub.id);
                        return (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<MapIcon />}
                            onClick={() => {
                              setHistoryMapOpenIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(sub.id)) next.delete(sub.id);
                                else next.add(sub.id);
                                return next;
                              });
                            }}
                          >
                            {open ? 'Hide map' : 'Show map'}
                          </Button>
                        );
                      })()}
                      {sub.status === 'pending' && (
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          onClick={() => handleCancelSubmissionOpen(sub.id)}
                        >
                          Withdraw Submission
                        </Button>
                      )}
                      {sub.status === 'approved' && !sub.reverted && (
                        <Tooltip title="Undo this change — restores the previous ZIPs and updates your campaigns automatically.">
                          <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            startIcon={<HistoryIcon fontSize="small" />}
                            onClick={() => setRevertTarget(sub)}
                          >
                            Revert
                          </Button>
                        </Tooltip>
                      )}
                    </Box>

                    {historyMapOpenIds.has(sub.id) && (
                      <Box mt={1.5}>
                        <SubmissionDiffMap items={sub.items || []} submissionIds={[sub.id]} />
                      </Box>
                    )}
                  </Paper>
                );
              })}

              {/* Load more */}
              {historyHasMore && (
                <Box display="flex" justifyContent="center" mt={1}>
                  <Button
                    variant="outlined"
                    onClick={() => loadHistory(historyPage + 1, historyFilter, true)}
                    disabled={historyLoading}
                    startIcon={historyLoading ? <CircularProgress size={16} /> : null}
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      )}

      {/* ─── Pause History Dialog ─────────────────────────────────────── */}
      <Dialog
        open={Boolean(pauseHistoryTerritory)}
        onClose={() => setPauseHistoryTerritory(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Pause history — {pauseHistoryTerritory?.name}
        </DialogTitle>
        <DialogContent>
          {pauseHistoryEvents === null && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          )}
          {pauseHistoryEvents !== null && pauseHistoryEvents.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No pause or resume events recorded for this territory yet.
              History is kept from the moment this feature shipped — older
              toggles weren&apos;t logged.
            </Typography>
          )}
          {pauseHistoryEvents !== null && pauseHistoryEvents.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">ZIPs</TableCell>
                  <TableCell>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pauseHistoryEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <Tooltip title={new Date(event.created_at).toLocaleString()}>
                        <span>
                          {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={event.paused ? 'Paused' : 'Resumed'}
                        color={event.paused ? 'warning' : 'success'}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>{event.vertical || 'Whole market'}</TableCell>
                    <TableCell align="right">{event.zip_count}</TableCell>
                    <TableCell>{event.actor || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseHistoryTerritory(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Create Territory Dialog ──────────────────────────────────── */}
      <Dialog
        open={newTerritoryDialogOpen}
        onClose={() => setNewTerritoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Territory</DialogTitle>
        <DialogContent>
          {newTerritoryError && (
            <Alert severity="error" sx={{ mb: 2 }}>{newTerritoryError}</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Territory Name"
            value={newTerritoryName}
            onChange={(e) => setNewTerritoryName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTerritoryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTerritory}
            disabled={newTerritorySaving}
          >
            {newTerritorySaving ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Rename Territory Dialog ──────────────────────────────────── */}
      <Dialog
        open={renameTerritoryDialogOpen}
        onClose={() => setRenameTerritoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Territory</DialogTitle>
        <DialogContent>
          {renameTerritoryError && (
            <Alert severity="error" sx={{ mb: 2 }}>{renameTerritoryError}</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="New Name"
            value={renameTerritoryName}
            onChange={(e) => setRenameTerritoryName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTerritoryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRenameTerritory}
            disabled={renameTerritorySaving}
          >
            {renameTerritorySaving ? <CircularProgress size={18} /> : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Manage ZIPs Dialog ───────────────────────────────────────── */}
      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography variant="h6" component="div">
            Manage ZIPs — {manageTerritory?.name}
            {' / '}
            {isMultiVerticalManage
              ? `${manageVerticals.length} verticals`
              : manageAnchorVertical?.vertical}
          </Typography>
          {isMultiVerticalManage && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}>
              <Tooltip title="Changes will apply to all of these verticals — one submission per vertical, grouped as one batch.">
                <Chip
                  label="Applies to all"
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
              {manageVerticals.map((v) => (
                <Chip
                  key={v.id}
                  label={v.vertical}
                  size="small"
                  variant="outlined"
                />
              ))}
              <Box flexGrow={1} />
              <Button
                size="small"
                variant="text"
                onClick={(e) => setCustomizeAnchorEl(e.currentTarget)}
                sx={{ textTransform: 'none' }}
              >
                Customize per vertical
              </Button>
              <Menu
                anchorEl={customizeAnchorEl}
                open={Boolean(customizeAnchorEl)}
                onClose={() => setCustomizeAnchorEl(null)}
              >
                {manageVerticals.map((v) => (
                  <MenuItem
                    key={v.id}
                    onClick={() => {
                      setCustomizeAnchorEl(null);
                      handleOpenManageDialog(manageTerritory, v);
                    }}
                  >
                    Edit {v.vertical} only
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {manageError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={manageMoveOffer?.addZips?.length ? (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  disabled={manageSubmitting}
                  onClick={() => submitAsMove(manageMoveOffer.addZips)}
                >
                  Move them here instead
                </Button>
              ) : undefined}
            >
              {manageError}
            </Alert>
          )}

          <Tabs
            value={manageMode}
            onChange={(_, v) => {
              setManageMode(v);
              setManageZipText('');
              setManageRemoveIds([]);
              setManageValidation(null);
              setManageError('');
              setManageMoveOffer(null);
            }}
            sx={{ mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="add" label="Add ZIPs" />
            <Tab value="remove" label="Remove ZIPs" />
            <Tab value="move" label="Move ZIPs Here" />
            <Tab value="replace" label="Replace Full List" />
          </Tabs>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {manageMode === 'add' && 'Paste ZIP codes to add (comma, tab, or newline separated) or upload a CSV file.'}
            {manageMode === 'replace' && 'Paste the complete final list of ZIPs for this territory. ZIPs not in your list are removed, new ones are added — other territories are never affected.'}
            {manageMode === 'remove' && 'Paste ZIP codes to remove from this territory. This shrinks your service area — if you only want to stop ads temporarily, use Pause on the territory instead.'}
            {manageMode === 'move' && 'Paste ZIP codes to bring into this territory. ZIPs that currently belong to another territory are transferred here automatically — no need to remove them from the old territory first.'}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.tsv"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            {manageMode !== 'remove' && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => setCopyFromAnchorEl(e.currentTarget)}
                disabled={copySourceOptions.length === 0}
              >
                {manageMode === 'move' ? 'Load ZIPs from another territory' : 'Copy from another vertical'}
              </Button>
            )}
            <Menu
              open={Boolean(copyFromAnchorEl)}
              anchorEl={copyFromAnchorEl}
              onClose={() => setCopyFromAnchorEl(null)}
            >
              {copySourceOptions.length === 0 ? (
                <MenuItem disabled>No other verticals with ZIPs</MenuItem>
              ) : (
                copySourceOptions.map((opt) => (
                  <MenuItem
                    key={opt.key}
                    onClick={() => {
                      const zipCodes = opt.zips.map((z) => z.zip).join('\n');
                      setManageZipText(zipCodes);
                      setCopyFromAnchorEl(null);
                    }}
                  >
                    <Stack direction="column" spacing={0} alignItems="flex-start">
                      <Typography variant="body2" fontWeight={600}>
                        {opt.territoryName} / {opt.verticalName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.zips.length.toLocaleString()} standard ZIP{opt.zips.length === 1 ? '' : 's'}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))
              )}
            </Menu>
          </Stack>

          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="10001, 10002, 10003..."
            value={manageZipText}
            onChange={(e) => setManageZipText(e.target.value)}
            sx={{ mb: 1 }}
          />

          {/* Live validation feedback */}
          {manageValidation && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'background.default' }}>
              <Stack spacing={0.75}>
                {manageMode === 'remove' ? (
                  <>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        label={`${manageValidation.matched?.length || 0} to remove`}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                      {(manageValidation.notFound?.length || 0) > 0 && (
                        <Chip
                          label={`${manageValidation.notFound.length} not found (skipped)`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {manageValidation.matched?.length > 0 && (
                      <Typography variant="caption" color="error.main">
                        Ready to submit removal of {manageValidation.matched.length} ZIP{manageValidation.matched.length > 1 ? 's' : ''}.
                      </Typography>
                    )}
                  </>
                ) : manageMode === 'replace' ? (
                  <>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      {manageValidation.newZips.length > 0 && (
                        <Chip
                          label={`${manageValidation.newZips.length} to add`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {(manageValidation.toRemove?.length || 0) > 0 && (
                        <Chip
                          label={`${manageValidation.toRemove.length} to remove`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      )}
                      {manageValidation.dupes.length > 0 && (
                        <Chip
                          label={`${manageValidation.dupes.length} unchanged`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                      {manageValidating && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">Checking...</Typography>
                        </Stack>
                      )}
                    </Box>
                    {manageValidation.apiChecked && manageValidation.unknown.length > 0 && (
                      <Alert
                        severity="error"
                        variant="outlined"
                        sx={{ py: 0.25 }}
                        action={
                          <Button size="small" color="error" onClick={handleRemoveInvalidZips}>
                            Remove
                          </Button>
                        }
                      >
                        <Typography variant="caption">
                          Unknown ZIPs: {manageValidation.unknown.slice(0, 10).join(', ')}
                          {manageValidation.unknown.length > 10 ? ` +${manageValidation.unknown.length - 10} more` : ''}
                        </Typography>
                      </Alert>
                    )}
                    {manageValidation.apiChecked && manageValidation.conflicts.length > 0 && (
                      <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                        <Typography variant="caption">
                          Conflicts: {manageValidation.conflicts.slice(0, 5).map((c) => `${c.zip} (${c.current_territory})`).join(', ')}
                          {manageValidation.conflicts.length > 5 ? ` +${manageValidation.conflicts.length - 5} more` : ''}
                        </Typography>
                      </Alert>
                    )}
                    {manageValidation.apiChecked
                      && manageValidation.unknown.length === 0
                      && manageValidation.conflicts.length === 0 && (
                      <Typography variant="caption" color="success.main">
                        Validated — {manageValidation.newZips.length} add{manageValidation.newZips.length !== 1 ? 's' : ''}, {manageValidation.toRemove?.length || 0} removal{(manageValidation.toRemove?.length || 0) !== 1 ? 's' : ''}.
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        label={`${manageValidation.newZips.length} new`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      {manageValidation.dupes.length > 0 && (
                        <Chip
                          label={`${manageValidation.dupes.length} already active (skipped)`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                      {manageValidation.inputDupes > 0 && (
                        <Chip
                          label={`${manageValidation.inputDupes} duplicate input${manageValidation.inputDupes > 1 ? 's' : ''}`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                      {manageValidating && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">Checking...</Typography>
                        </Stack>
                      )}
                    </Box>

                    {manageValidation.apiChecked && manageValidation.unknown.length > 0 && (
                      <Alert
                        severity="error"
                        variant="outlined"
                        sx={{ py: 0.25 }}
                        action={
                          <Button size="small" color="error" onClick={handleRemoveInvalidZips}>
                            Remove
                          </Button>
                        }
                      >
                        <Typography variant="caption">
                          Unknown ZIPs: {manageValidation.unknown.slice(0, 10).join(', ')}
                          {manageValidation.unknown.length > 10 ? ` +${manageValidation.unknown.length - 10} more` : ''}
                        </Typography>
                      </Alert>
                    )}

                    {manageValidation.apiChecked && manageValidation.conflicts.length > 0 && (
                      manageMode === 'move' ? (
                        <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
                          <Typography variant="caption">
                            {manageValidation.conflicts.length} ZIP{manageValidation.conflicts.length > 1 ? 's' : ''} will transfer here from&nbsp;
                            {[...new Set(manageValidation.conflicts.map((c) => c.current_territory))].slice(0, 3).join(', ')}
                            : {manageValidation.conflicts.slice(0, 5).map((c) => c.zip).join(', ')}
                            {manageValidation.conflicts.length > 5 ? ` +${manageValidation.conflicts.length - 5} more` : ''}
                          </Typography>
                        </Alert>
                      ) : (
                        <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                          <Typography variant="caption">
                            Already in another territory: {manageValidation.conflicts.slice(0, 5).map((c) => `${c.zip} (${c.current_territory})`).join(', ')}
                            {manageValidation.conflicts.length > 5 ? ` +${manageValidation.conflicts.length - 5} more` : ''}
                            {' '}— submitting will offer to move them here.
                          </Typography>
                        </Alert>
                      )
                    )}

                    {manageValidation.apiChecked
                      && manageValidation.unknown.length === 0
                      && (manageValidation.conflicts.length === 0 || manageMode === 'move')
                      && manageValidation.newZips.length > 0 && (
                      <>
                        <Typography variant="caption" color="success.main">
                          All ZIPs validated — ready to submit.
                        </Typography>
                        {manageValidation.zipDetails && Object.keys(manageValidation.zipDetails).length > 0 && (
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {manageValidation.newZips.slice(0, 30).map((z) => {
                              const d = manageValidation.zipDetails[z];
                              return (
                                <Tooltip key={z} title={d?.city && d?.state ? `${d.city}, ${d.state}` : ''} arrow>
                                  <Chip label={z} size="small" color="success" variant="outlined" />
                                </Tooltip>
                              );
                            })}
                            {manageValidation.newZips.length > 30 && (
                              <Chip label={`+${manageValidation.newZips.length - 30} more`} size="small" />
                            )}
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          )}

          {/* Visual change preview — same green/red diff map clients see on
              their submission receipts, shown BEFORE they submit. */}
          {manageDiffMapItems.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Change preview — <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>green = adding</Box>
                {manageDiffMapItems.some((i) => i.action === 'remove') && (
                  <>, <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>red = removing</Box></>
                )}
              </Typography>
              <SubmissionDiffMap items={manageDiffMapItems} height={220} />
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Current ZIPs ({currentManagedZips.length})
            {manageMode === 'add' && manageRemoveIds.length > 0 && (
              <Chip
                label={`${manageRemoveIds.length} marked for removal`}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
            {manageMode === 'replace' && manageValidation?.toRemove?.length > 0 && (
              <Chip
                label={`${manageValidation.toRemove.length} will be removed`}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          {manageMode === 'add' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Click a ZIP to toggle it for removal.
            </Typography>
          )}
          <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ maxHeight: 300, overflow: 'auto' }}>
            {currentManagedZips.map((czv) => {
              const isMarkedForRemoval = manageMode === 'add'
                ? manageRemoveIds.includes(czv.id)
                : manageMode === 'replace'
                  ? manageValidation?.toRemove?.some((r) => r.id === czv.id)
                  : manageValidation?.matched?.includes(czv.zip);
              return (
                <Tooltip key={czv.id} title={czv.city && czv.state ? `${czv.city}, ${czv.state}` : ''} arrow>
                  <Chip
                    label={czv.zip}
                    size="small"
                    color={isMarkedForRemoval ? 'error' : 'default'}
                    variant={isMarkedForRemoval ? 'filled' : 'outlined'}
                    onClick={manageMode === 'add' ? () => handleToggleRemove(czv.id) : undefined}
                    sx={manageMode === 'add' ? { cursor: 'pointer' } : undefined}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <TextField
            label="Note (optional)"
            placeholder="e.g. expanding to south FL, seasonal pause..."
            value={submitterNotes}
            onChange={(e) => setSubmitterNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mt: 2 }}
          />
          <TextField
            type="date"
            label="When should this market start? (optional)"
            helperText="Leave blank to go live as soon as approved. With a date, the ZIPs are held until then. If you budget by territory, we'll draft this territory's segment for you to fund."
            value={launchOn}
            onChange={(e) => setLaunchOn(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitZipChanges}
            disabled={manageSubmitting}
            startIcon={manageSubmitting ? <CircularProgress size={16} /> : null}
          >
            Submit for Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Revert Submission Confirm ────────────────────────────────── */}
      <Dialog
        open={Boolean(revertTarget)}
        onClose={() => !revertSubmitting && setRevertTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revert this change?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This undoes the change to{' '}
            <strong>{revertTarget?.territory_name || 'this territory'}</strong>
            {revertTarget?.vertical_name ? ` / ${revertTarget.vertical_name}` : ''}:
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
            {(revertTarget?.remove_count || 0) > 0 && (
              <Typography variant="body2" color="success.main">
                +{revertTarget.remove_count} ZIP{revertTarget.remove_count !== 1 ? 's' : ''} restored
              </Typography>
            )}
            {(revertTarget?.add_count || 0) > 0 && (
              <Typography variant="body2" color="error.main">
                −{revertTarget.add_count} ZIP{revertTarget.add_count !== 1 ? 's' : ''} removed again
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            The revert applies automatically and your campaigns update on their
            own. ZIPs that have since moved to another territory are left where
            they are.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertTarget(null)} disabled={revertSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRevert}
            disabled={revertSubmitting}
            startIcon={revertSubmitting ? <CircularProgress size={16} /> : null}
          >
            Revert Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Pause Territory Confirm ──────────────────────────────────── */}
      <Dialog
        open={Boolean(pauseConfirmTerritory)}
        onClose={() => setPauseConfirmTerritory(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Pause {pauseConfirmTerritory?.name}?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Pausing temporarily stops your ads in this territory. It takes effect
            in about 2 minutes and doesn&apos;t need a review.
          </Typography>
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            <Typography variant="body2">• All ZIPs, spend history, and reporting are kept</Typography>
            <Typography variant="body2">• No leads or ad spend while paused</Typography>
            <Typography variant="body2">• Resume anytime with one click</Typography>
          </Stack>
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            <Typography variant="caption">
              Looking to permanently stop serving certain areas? Use{' '}
              <strong>Manage ZIPs → Remove ZIPs</strong> instead — that shrinks
              your coverage{autoReviewOn
                ? ` (you get a ${undoWindowMin}-minute undo window; large reductions are reviewed by our team)`
                : ' and goes through a quick review'}.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseConfirmTerritory(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              const t = pauseConfirmTerritory;
              setPauseConfirmTerritory(null);
              handleTogglePause(t, true);
            }}
          >
            Pause Territory
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Bulk Upload Dialog ───────────────────────────────────────── */}
      <BulkZipUploadDialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        clientId={selectedClientId || undefined}
        parentCompanyId={isAllClientsView ? selectedParentCompanyId : undefined}
        territories={territories}
        allVerticals={allVerticals}
        clientOptions={clientOptions}
        onSubmitted={handleBulkSubmitted}
      />

      {/* ─── Withdraw Submission Dialog ────────────────────────────────── */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Withdraw Submission</DialogTitle>
        <DialogContent>
          <Typography>
            {(() => {
              const sub = submissions.find((s) => s.id === cancelSubmissionId);
              const auto = autoReviewOn ? pendingAutoState(sub, nowTs) : null;
              return auto && auto.kind !== 'hold'
                ? 'Cancel this change before it applies? Your coverage stays as it is now, and you can correct the ZIPs and resubmit. This can’t be undone.'
                : 'Withdraw this pending submission? It will be removed from review so you can correct the ZIPs and resubmit. This can’t be undone.';
            })()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelSaving}>
            Keep
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubmissionConfirm}
            disabled={cancelSaving}
            startIcon={cancelSaving ? <CircularProgress size={16} /> : null}
          >
            Withdraw
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Submission Confirmation Dialog ────────────────────────────── */}
      <Dialog
        open={!!confirmationDialog}
        onClose={() => setConfirmationDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Changes Submitted
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your ZIP changes for <strong>{confirmationDialog?.territory} / {confirmationDialog?.vertical}</strong> are
            on their way.
          </Typography>
          {confirmationDialog?.submissionCount > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Created {confirmationDialog.submissionCount} submissions (one per vertical), grouped as one batch.
            </Typography>
          )}
          <Stack direction="row" spacing={2} justifyContent="center">
            {confirmationDialog?.addCount > 0 && (
              <Chip
                label={`${confirmationDialog.addCount} ZIP${confirmationDialog.addCount !== 1 ? 's' : ''} to add`}
                color="success"
                variant="outlined"
              />
            )}
            {confirmationDialog?.removeCount > 0 && (
              <Chip
                label={`${confirmationDialog.removeCount} ZIP${confirmationDialog.removeCount !== 1 ? 's' : ''} to remove`}
                color="error"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {(() => {
              const ar = confirmationDialog?.autoReview;
              if (ar?.enabled === false || (!ar && !autoReviewOn)) {
                return 'Our team will review your changes shortly — you’ll get an email once they’re live.';
              }
              if (ar?.tier === 'hold') {
                return 'This change is a large reduction to your coverage, so it’s been sent to our team for review. We’ll email you once it’s processed.';
              }
              if (ar?.tier && ar.delayed) {
                return `Applies in ~${ar.apply_delay_minutes || undoWindowMin} minutes — you can undo from the Coverage tab until then. We’ll email you once it’s live.`;
              }
              if (ar?.tier) {
                return 'Applied — pushing to your campaigns now. We’ll email you once everything is live.';
              }
              // Tier unknown (e.g. bulk upload) — keep the generic truth.
              return 'Most changes apply automatically within minutes — you’ll get an email once they’re live. Large reductions to your coverage get a quick review from our team first.';
            })()}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button variant="contained" onClick={() => setConfirmationDialog(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add Vertical Dialog ─────────────────────────────────────── */}
      <Dialog
        open={addVerticalDialogOpen}
        onClose={() => setAddVerticalDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Verticals</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select verticals you want to manage ZIPs for. They will appear in the
            territory dropdowns below.
          </Typography>
          <Stack spacing={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            {allVerticals.map((v) => {
              const alreadyActive = verticals.some((av) => av.id === v.id);
              const isAdded = addedVerticalIds.has(v.id);
              return (
                <Box
                  key={v.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: alreadyActive || isAdded ? 'action.selected' : 'transparent',
                  }}
                >
                  <Typography variant="body2">{v.vertical}</Typography>
                  {alreadyActive ? (
                    <Chip label="Active" size="small" color="success" variant="outlined" />
                  ) : (
                    <Switch
                      size="small"
                      checked={isAdded}
                      onChange={() => {
                        setAddedVerticalIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(v.id)) {
                            next.delete(v.id);
                          } else {
                            next.add(v.id);
                          }
                          return next;
                        });
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVerticalDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Snackbar ─────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ─── Delete Market Confirmation (staff only) ──────────────────── */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => { if (!deleteSaving) setDeleteTarget(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteOutlineIcon color="error" />
          Delete Market
        </DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <>
              <Typography variant="body1" gutterBottom>
                Permanently remove <strong>{deleteTarget.name}</strong>?
              </Typography>
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                This deletes the territory and removes all its ZIPs from Facebook
                targeting. Unlike Pause, this does <strong>not</strong> keep the
                ZIPs for spend attribution — use it only for mistaken or retired
                markets. To stop a market while keeping its spend history, use
                Pause instead.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteSaving}
            startIcon={deleteSaving ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
          >
            {deleteSaving ? 'Deleting...' : 'Delete Market'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Welcome Overlay (first-time visitors) ────────────────────── */}
      {welcomeOpen && (
        <WelcomeOverlay onStartTour={handleWelcomeStartTour} onSkip={handleWelcomeSkip} />
      )}

      {/* ─── Walkthrough ──────────────────────────────────────────────── */}
      {walkthroughActive && (
        <WalkthroughOverlay
          stepIndex={walkthroughStep}
          steps={WALKTHROUGH_STEPS}
          stepRefs={walkthroughRefs}
          onNext={nextWalkthroughStep}
          onBack={prevWalkthroughStep}
          onSkip={endWalkthrough}
        />
      )}
    </Box>
  );
}

// ─── Helper Components ─────────────────────────────────────────────

const STAT_ICONS = {
  Territories: MapIcon,
  'Active ZIPs': PlaceIcon,
  Population: PeopleIcon,
  Verticals: LayersIcon,
  Pending: PendingActionsIcon,
};

const STAT_COLORS = {
  Territories: '#1976d2',
  'Active ZIPs': '#2e7d32',
  Population: '#7b1fa2',
  Verticals: '#0288d1',
  Pending: '#ed6c02',
};

function StatCard({ label, value, subtitle = null, highlight = false }) {
  const IconComp = STAT_ICONS[label];
  const accent = STAT_COLORS[label] || '#666';
  return (
    <Card
      variant="outlined"
      sx={{
        minWidth: 130,
        flex: '1 1 0',
        borderTop: `3px solid ${highlight ? '#ed6c02' : accent}`,
        ...(highlight && {
          bgcolor: 'warning.50',
        }),
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          {IconComp && <IconComp sx={{ fontSize: 18, color: accent }} />}
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function StepRow({ number, text }) {
  return (
    <Box display="flex" alignItems="center" gap={1.5} width="100%">
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.8rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography variant="body2" textAlign="left">
        {text}
      </Typography>
    </Box>
  );
}
